<!--
  Shop.vue — Star Shop page (Vue 3, <script setup>)
  Five spend mechanics + star pack staircase.
-->
<script setup>
import { ref, computed, reactive, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { trackEvent, trackStarShopBounceEvent } from '@/Helpers/analytics'
import { openStarPackPurchaseUrl } from '@/Helpers/starPackPurchase'
import HeaderV2 from '@/Components/Reels/HeaderV2.vue'
import BuyStarsModal from '@/Components/Reels/BuyStarsModal.vue'
import MembershipUpsellModal from '@/Components/StarShop/MembershipUpsellModal.vue'
import InterstitialAdModal from '@/Components/StarShop/InterstitialAdModal.vue'
import StarIcon from './StarIcon.vue'
import StarMarkSVG from './StarMarkSVG.vue'
import ShareStarProductButton from './ShareStarProductButton.vue'
import CelebrationBurst from './CelebrationBurst.vue'
import FeaturedProgress from './FeaturedProgress.vue'
import RowShell from './RowShell.vue'
import { RefreshCw, ChevronLeft, ChevronRight, Play } from 'lucide-vue-next'
import ShopAllModal from './ShopAllModal.vue'
import DiscountProductCard from './DiscountProductCard.vue'
import DirectAccessProductCard from './DirectAccessProductCard.vue'
import MembersChoiceProductCard from './MembersChoiceProductCard.vue'
import { useImpressionTracking } from '@/composables/useImpressionTracking'


const props = defineProps({
    starPacks:           { type: Array,  default: () => [] },
    flowers:             { type: Array,  default: () => [] },
    ofLinks:             { type: Array,  default: () => [] },
    directAccess:        { type: Array,  default: () => [] },
    vaultClips:          { type: Array,  default: () => [] },
    communityGoals:      { type: Array,  default: () => [] },
    membersChoice:       { type: Array,  default: () => [] },
    featuredGoals:       { type: Array,  default: () => [] },
    totals:              { type: Object, default: () => ({}) },
    highlightProductId:  { type: Number, default: null },
    shopUrl:             { type: String, default: '' },
    analyticsContext:    { type: Object, default: () => ({}) },
})

const page = usePage()
const balance = computed(() => Number(page.props.auth?.user?.wallet_balance) || 0)
const isUserLoggedIn = computed(() => !!page.props.auth?.user?.id)
const isGoldUser = computed(() => page.props.auth?.is_gold ?? false)
const requiresAgeVerification = computed(() => page.props.requiresAgeVerification ?? false)

// ─── Tweaks state ──────────────────────────────────────────────────────────
const SUBTYPE_KEY = { 1: 'remaster', 2: 'rebook', 3: 'xr', 4: 'series', 6: 'xr' }

function goalKey(fg) {
    if (fg.type === 1) return 'flowers'
    if (fg.type === 2) return 'oflink'
    if (fg.type === 5) return 'members_choice'
    return SUBTYPE_KEY[fg.subtype]
}

const mechanicByProductId = computed(() => {
    const map = {}
    for (const g of props.communityGoals) {
        map[g.id] = SUBTYPE_KEY[g.subtype] ?? 'unknown'
    }
    for (const m of props.membersChoice) {
        map[m.id] = 'members_choice'
    }
    for (const o of props.ofLinks) {
        map[o.product_id] = 'oflink'
    }
    for (const d of props.directAccess) {
        map[d.product_id] = 'direct_access'
    }
    for (const f of props.flowers) {
        map[f.product_id] = 'flowers'
    }
    // successModal.mechanic already special-cases 'vault_video' alongside
    // 'direct_access' (see successCopy computed below) — this was the one
    // mechanic missing from this map, so vault clip purchases tracked as
    // product_type/product_name: null everywhere on this page (including
    // the interstitial ad context) until now.
    for (const v of props.vaultClips) {
        map[v.product_id] = 'vault_video'
    }
    return map
})

const productNameById = computed(() => {
    const map = {}
    for (const g of props.communityGoals) map[g.id] = g.name
    for (const m of props.membersChoice) map[m.id] = m.name
    for (const o of props.ofLinks) map[o.product_id] = o.name
    for (const f of props.flowers) map[f.product_id] = f.name
    // directAccess/vaultClips were missing here too (see mechanicByProductId
    // above) — both share the same DirectAccessProductCard item shape, so
    // `name` is present on both.
    for (const d of props.directAccess) map[d.product_id] = d.name
    for (const v of props.vaultClips) map[v.product_id] = v.name
    return map
})

// Performer-bound ofLinks, flowers and directAccess items carry the performer name in `name`;
// items without a performer_id use the product name there instead, so they're excluded (see StarShopTrait).
const performerNameById = computed(() => {
    const map = {}
    for (const g of props.communityGoals) map[g.id] = g.performer_name ?? null
    for (const m of props.membersChoice) map[m.id] = m.performer_name ?? null
    for (const o of props.ofLinks) map[o.product_id] = o.performer_id ? o.name : null
    for (const f of props.flowers) map[f.product_id] = f.performer_id ? f.name : null
    for (const d of props.directAccess) map[d.product_id] = d.performer_id ? d.name : null
    return map
})

const performerPhotoById = computed(() => {
    const map = {}
    for (const g of props.communityGoals) map[g.id] = g.performer_photo ?? null
    for (const m of props.membersChoice) map[m.id] = m.performer_photo ?? null
    for (const o of props.ofLinks) map[o.product_id] = o.performer_id ? o.photo : null
    for (const f of props.flowers) map[f.product_id] = f.performer_id ? f.photo : null
    for (const d of props.directAccess) map[d.product_id] = d.performer_id ? d.photo : null
    return map
})

const offersCount = computed(() => props.communityGoals.length + props.membersChoice.length + props.ofLinks.length + props.flowers.length)

/**
 * Shared LTI Analytics base props — same shape as $sswBase in
 * star_shop_widget.blade.php and baseProps() in buy_stars_modal.blade.php.
 * Pass a productId to fill the product context when the event concerns one.
 */
function track(event, extra = {}, productId = null) {
    trackEvent(event, {
        source: 'star_shop_page',
        location: props.analyticsContext?.location ?? 'shop_page',
        location_id: null,
        star_balance: balance.value,
        offers_count: offersCount.value,
        cart_enabled: null,
        membership_lifecycle_stage: props.analyticsContext?.membership_lifecycle_stage ?? null,
        membership_term: props.analyticsContext?.membership_term ?? null,
        membership_tier: props.analyticsContext?.membership_tier ?? null,
        is_trial: props.analyticsContext?.is_trial ?? null,
        user_id: page.props.auth?.user?.id ?? null,
        product_id: productId,
        product_type: productId ? (mechanicByProductId.value[productId] ?? null) : null,
        product_name: productId ? (productNameById.value[productId] ?? null) : null,
        ...extra,
    })
}

// Member's Choice products page one at a time (arrows/dots), but the track
// itself is a native scroll-snap row (touch swipe + trackpad) with each card
// sized to peek the next one — same track on mobile and desktop now, so
// swipe/scroll works everywhere the arrows do. mcIndex tracks which card is
// "active" for the dots; it's driven FROM scroll position (onMcTrackScroll)
// rather than the other way around, so a user swipe updates the dots without
// a watcher trying to re-scroll the track it's reacting to.
const mcProducts = computed(() => props.membersChoice.slice(0, 3))
const mcIndex = ref(0)
const mcTrack = ref(null)
const mcAtStart = ref(true)
const mcAtEnd = ref(false)

function mcCardStep() {
    const track = mcTrack.value
    const first = track?.children?.[0]
    if (!first) return 0
    const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0
    return first.getBoundingClientRect().width + gap
}

// Rapid repeat clicks on an arrow would otherwise land while the previous
// smooth scrollBy is still animating; scrollBy's distance is relative to
// whatever scrollLeft is AT THAT MOMENT (mid-animation), so repeated calls
// compound and overshoot past the intended single card. Locking for one
// call at a time (cleared after the scroll settles) makes one click worth
// exactly one card-step.
let mcNavLocked = false

function mcNavigate(step) {
    if (mcNavLocked) return
    mcNavLocked = true
    mcTrack.value?.scrollBy({ left: step, behavior: 'smooth' })
    setTimeout(() => { mcNavLocked = false }, 500)
}

function trackMcCarouselNav(method, toIndex) {
    track('Star Shop Members Choice Carousel Navigated', {
        method,
        direction: toIndex > mcIndex.value ? 'next' : 'prev',
        from_product_id: mcProducts.value[mcIndex.value]?.id ?? null,
        to_product_id: mcProducts.value[toIndex]?.id ?? null,
    })
}

function mcPrev() {
    trackMcCarouselNav('arrow', Math.max(0, mcIndex.value - 1))
    mcNavigate(-mcCardStep())
}

function mcNext() {
    trackMcCarouselNav('arrow', Math.min(mcProducts.value.length - 1, mcIndex.value + 1))
    mcNavigate(mcCardStep())
}

function mcGoTo(i) {
    trackMcCarouselNav('dot', i)
    const card = mcTrack.value?.children?.[i]
    card?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
}

function trackMcOptionViewed(productId, optionId) {
    track('Star Shop Members Choice Option Viewed', { option_id: optionId }, productId)
}

function trackMcCtaClick(productId, cta) {
    track('Star Shop Members Choice CTA Clicked', {
        cta_type: cta.label.toLowerCase().includes('casting') ? 'casting' : 'audition',
        cta_label: cta.label,
        cta_url: cta.url,
    }, productId)
}

// rAF-throttled so rapid scroll/fling events don't run the index math on every
// single scroll tick — mirrors the debounce pattern used for vote batching.
let mcScrollRaf = null

function updateMcIndexFromScroll() {
    const track = mcTrack.value
    if (!track) return
    mcAtStart.value = track.scrollLeft <= 1
    mcAtEnd.value = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1
    let closest = 0
    let closestDist = Infinity
    Array.from(track.children).forEach((el, i) => {
        const dist = Math.abs(el.offsetLeft - track.scrollLeft)
        if (dist < closestDist) {
            closestDist = dist
            closest = i
        }
    })
    mcIndex.value = closest
}

function onMcTrackScroll() {
    if (mcScrollRaf) cancelAnimationFrame(mcScrollRaf)
    mcScrollRaf = requestAnimationFrame(updateMcIndexFromScroll)
}

// Arrows/dots and manual swipe all funnel through scroll (see the comment
// above mcIndex), so watching it here — rather than adding a pause call to
// each nav path separately — catches every way the active product can
// change. Otherwise a video left playing in the product scrolled away from
// keeps playing (and audible) behind the newly active one.
watch(mcIndex, () => {
    mcTrack.value?.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause() })
})

onMounted(() => {
    nextTick(updateMcIndexFromScroll)
})

// Vault Clips carousel — several cards peek at once (unlike Member's Choice's
// one-at-a-time pager), so it only needs start/end-aware arrows, not dots.
const vcTrack = ref(null)
const vcAtStart = ref(true)
const vcAtEnd = ref(false)

function vcCardStep() {
    const track = vcTrack.value
    const first = track?.children?.[0]
    if (!first) return 0
    const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0
    return first.getBoundingClientRect().width + gap
}

let vcNavLocked = false

function vcNavigate(step) {
    if (vcNavLocked) return
    vcNavLocked = true
    vcTrack.value?.scrollBy({ left: step, behavior: 'smooth' })
    setTimeout(() => { vcNavLocked = false }, 500)
}

function vcPrev() {
    track('Star Shop Vault Clip Carousel Navigated', { method: 'arrow', direction: 'prev' })
    vcNavigate(-vcCardStep())
}

function vcNext() {
    track('Star Shop Vault Clip Carousel Navigated', { method: 'arrow', direction: 'next' })
    vcNavigate(vcCardStep())
}

// One event name shared by every DirectAccessProductCard instance on this
// page (vault clips row, plain Direct Access grid, "see all" modal) — same
// convention as the Purchase events, which distinguish by product_type/mechanic
// (already included via track()'s productId lookup) rather than a separate
// event name per section.
function trackItemViewed(productId) {
    track('Star Shop Item Viewed', {}, productId)
}

function trackItemDownloaded(productId) {
    track('Star Shop Item Downloaded', {}, productId)
}

let vcScrollRaf = null

function updateVcEdgesFromScroll() {
    const track = vcTrack.value
    if (!track) return
    vcAtStart.value = track.scrollLeft <= 1
    vcAtEnd.value = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1
}

function onVcTrackScroll() {
    if (vcScrollRaf) cancelAnimationFrame(vcScrollRaf)
    vcScrollRaf = requestAnimationFrame(updateVcEdgesFromScroll)
}

onMounted(() => {
    nextTick(updateVcEdgesFromScroll)
})

const featuredIndex = ref(null)

// Derived display key for CSS class and template branching.
// Multiple featured goals can share the same key (e.g. two OF links both map to 'oflink'),
// so rotation tracks by index rather than by key.
const featuredKey = computed(() => {
    if (featuredIndex.value === null) return null
    const fg = props.featuredGoals[featuredIndex.value]
    return fg ? goalKey(fg) : null
})

const currentFeaturedImage = computed(() =>
    featuredIndex.value !== null ? (props.featuredGoals[featuredIndex.value]?.image ?? null) : null
)

const copied = ref(null)

function copyShareLink(productId) {
    const url = props.shopUrl + '?product=' + productId
    const done = () => {
        copied.value = productId
        setTimeout(() => { if (copied.value === productId) copied.value = null }, 2000)
    }
    const fallback = () => {
        const el = document.createElement('textarea')
        el.value = url
        el.style.position = 'fixed'
        el.style.opacity = '0'
        document.body.appendChild(el)
        el.focus()
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        done()
    }
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(done).catch(fallback)
    } else {
        fallback()
    }
    track('Star Shop Share Link Copied', {}, productId)
}

onMounted(() => {
    window.addEventListener('pagehide', flushAllPendingVotes)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushAllPendingVotes()
    })
    // Capture campaign attribution (e.g. utm_source=na_transactional on award
    // email CTAs), then strip the params so a refresh doesn't re-count.
    const urlParams = new URLSearchParams(window.location.search)
    const entrySrc = urlParams.get('utm_source')
    const entryCampaign = urlParams.get('utm_campaign')
    const entryContent = urlParams.get('utm_content')
    if (entrySrc || entryCampaign) {
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((p) => urlParams.delete(p))
        const query = urlParams.toString()
        window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash)
    }
    track('Star Shop Page Viewed', {
        star_packs_count: props.starPacks.length,
        highlight_product_id: props.highlightProductId,
        entry_src: entrySrc,
        entry_campaign: entryCampaign,
        entry_content: entryContent,
    }, props.highlightProductId)
    if (props.featuredGoals.length) {
        featuredIndex.value = Math.floor(Math.random() * props.featuredGoals.length)
    }
    if (props.highlightProductId) {
        nextTick(() => {
            const el = document.querySelector(`[data-product-id="${props.highlightProductId}"]`)
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                el.classList.add('card--highlight')
                setTimeout(() => el.classList.remove('card--highlight'), 2500)
            }
        })
    }
})

function refreshFeatured() {
    if (props.featuredGoals.length <= 1) return
    const prev = featuredIndex.value
    let next = prev
    while (next === prev) {
        next = Math.floor(Math.random() * props.featuredGoals.length)
    }
    featuredIndex.value = next
}


const availableOfLinks        = computed(() => props.ofLinks.filter(o => o.available > 0))
const availableOfLinksOfLink  = computed(() => props.ofLinks.filter(o => o.discount_subtype === 1 && o.available > 0))
const availableOfLinksCoupon  = computed(() => props.ofLinks.filter(o => o.discount_subtype === 2 && o.available > 0))
const availableOfLinksFansly  = computed(() => props.ofLinks.filter(o => o.discount_subtype === 3 && o.available > 0))
const availableDirectAccess   = computed(() => props.directAccess.filter(o => o.available > 0 || o.unlocked))

const catalogTitle = computed(() => {
    const WORDS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen']
    const subtypes = new Set(props.communityGoals.map(g => g.subtype))
    let count = subtypes.size
    if (availableOfLinksOfLink.value.length > 0) count++
    if (availableOfLinksCoupon.value.length > 0) count++
    if (availableOfLinksFansly.value.length > 0) count++
    if (availableDirectAccess.value.length > 0) count++
    if (props.membersChoice.length > 0) count++
    if (props.flowers.length > 0) count++
    const word = WORDS[count - 1] ?? count
    return `${word} thing${count === 1 ? '' : 's'} you can spend stars on right now.`
})

const heroSub = computed(() => {
    const subtypes = new Set(props.communityGoals.map(g => g.subtype))
    const lines = [
        availableOfLinksOfLink.value.length > 0 && 'Unlock an OF discount.',
        availableOfLinksCoupon.value.length > 0 && 'Grab an exclusive coupon.',
        availableOfLinksFansly.value.length > 0 && 'Unlock a Fansly discount.',
        availableDirectAccess.value.length > 0 && 'Unlock exclusive clips.',
        subtypes.has(1) && 'Restore a classic.',
        subtypes.has(2) && 'Bring a performer back.',
        subtypes.has(3) && 'Greenlight an immersive shoot.',
        subtypes.has(4) && 'Revive a series.',
        props.membersChoice.length > 0 && "Crown the members' pick.",
        props.flowers.length  > 0 && 'Send real flowers to set.',
    ].filter(Boolean)

    if (!lines.length) return 'Every spend leaves a trace in the production roadmap.'

    const count = lines.length
    const mechanic = count === 1 ? 'mechanic' : 'mechanics'
    return `${lines.join(' ')} ${count} ${mechanic}, one wallet — every spend leaves a trace in the production roadmap.`
})
const sectionGap = 88

// ─── Expiry helpers ───────────────────────────────────────────────────────
const CLOSING_SOON_DAYS = 7

function expiryLabel(daysLeft) {
    if (daysLeft === null || daysLeft === undefined) return null
    if (daysLeft <= 0) return 'Closes today'
    if (daysLeft === 1) return '1 day left'
    if (daysLeft < 30) return `${daysLeft} days left`
    const months = Math.floor(daysLeft / 30)
    return months === 1 ? '1 month left' : `${months} months left`
}

function isClosingSoon(daysLeft) {
    return daysLeft !== null && daysLeft !== undefined && daysLeft <= CLOSING_SOON_DAYS
}

// ─── Section data (mapped from communityGoals prop) ───────────────────────
const remasters = computed(() =>
    props.communityGoals
        .filter(g => g.subtype === 1)
        .map(g => ({
            id:        g.id,
            starCost:  g.star_cost,
            title:     g.scene_title ?? g.name,
            sceneSlug: g.scene_slug ?? null,
            year:      g.scene_year ?? '',
            series:    g.scene_series ?? '',
            cover:     g.scene_cover ?? g.metadata?.cover ?? 'linear-gradient(135deg,#2a1d0a,#54421d)',
            coverIsImage: !!g.scene_cover,
            progress:  g.stars_raised,
            goal:      g.star_goal,
            backers:   g.backers ?? 0,
            pct:       g.pct,
            note:      g.description ?? '',
            hot:         g.metadata?.hot ?? false,
            daysLeft:    g.days_left ?? null,
            goalReached: g.goal_reached ?? false,
        }))
)

const bounties = computed(() =>
    props.communityGoals
        .filter(g => g.subtype === 2)
        .map(g => ({
            id:            g.id,
            starCost:      g.star_cost,
            name:          g.performer_name ?? g.name,
            performerSlug: g.performer_slug ?? null,
            photo:         g.performer_photo ?? null,
            lastSeen:      g.performer_last_seen ?? null,
            avatarTone:    g.metadata?.avatarTone ?? 'linear-gradient(135deg,#374151,#4B5563)',
            initials:      g.metadata?.initials ?? (g.performer_name ?? g.name).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(),
            progress:      g.stars_raised,
            goal:          g.star_goal,
            backers:       g.backers ?? 0,
            pct:           g.pct,
            momentum:      g.momentum ?? '',
            note:          g.description ?? '',
            daysLeft:      g.days_left ?? null,
            goalReached:   g.goal_reached ?? false,
        }))
)

const xrQueue = computed(() => {
    const sorted = [...props.communityGoals.filter(g => g.subtype === 3 || g.subtype === 6)]
        .sort((a, b) => b.stars_raised - a.stars_raised)
    return sorted.map((g, i) => ({
        id:       g.id,
        starCost: g.star_cost,
        rank:     i + 1,
        label:       g.subtype === 6 ? 'AR' : 'VR',
        name:        g.performer_name ?? g.name,
        productName: g.performer_name ? g.name : null,
        photo:       g.performer_photo ?? null,
        stars:       g.stars_raised,
        goal:        g.star_goal ?? null,
        pct:         g.pct ?? 0,
        you:         g.metadata?.you ?? 0,
        trend:       g.metadata?.trend ?? '+0%',
        initials:    g.metadata?.initials ?? (g.performer_name ?? g.name).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(),
        tone:        g.metadata?.tone ?? 'linear-gradient(135deg,#374151,#4B5563)',
        note:        g.description ?? '',
        daysLeft:    g.days_left ?? null,
        goalReached: g.goal_reached ?? false,
    }))
})

const seriesList = computed(() => {
    const goals = props.communityGoals.filter(g => g.subtype === 4)
    const maxVotes = goals.reduce((m, g) => Math.max(m, g.stars_raised), 0)
    return goals.map(g => ({
        id:        g.id,
        starCost:  g.star_cost,
        title:     g.name,
        dormant:   g.metadata?.dormant ?? '',
        cover:     g.metadata?.cover ?? 'linear-gradient(135deg,#eaeaea,#ffffff)',
        logo:      g.site_logo ?? null,
        votes:     g.stars_raised,
        voters:    g.backers ?? 0,
        yourVotes: g.metadata?.yourVotes ?? 0,
        triggerAt: g.star_goal,
        pct:       g.pct,
        note:      g.description || g.site_description || '',
        leading:     goals.length > 1 && g.stars_raised === maxVotes,
        daysLeft:    g.days_left ?? null,
        goalReached: g.goal_reached ?? false,
    }))
})

// ─── Featured slot shortcuts (pinned to the admin-curated featured list) ─────
const featuredIdBySubtype = computed(() => {
    const map = {}
    for (const fg of props.featuredGoals) {
        if (!(fg.subtype in map)) map[fg.subtype] = fg.id
    }
    return map
})

const featuredImageByKey = computed(() => {
    const map = {}
    for (const fg of props.featuredGoals) {
        const key = goalKey(fg)
        if (key && fg.image) map[key] = fg.image
    }
    return map
})

const featuredBounty = computed(() => {
    const id = featuredIdBySubtype.value[2]
    return (id ? bounties.value.find(b => b.id === id) : null) ?? bounties.value[0] ?? null
})
const featuredRemaster = computed(() => {
    const id = featuredIdBySubtype.value[1]
    return (id ? remasters.value.find(r => r.id === id) : null) ?? remasters.value[0] ?? null
})
const topXR = computed(() => {
    const id = featuredIdBySubtype.value[3]
    return (id ? xrQueue.value.find(x => x.id === id) : null) ?? xrQueue.value[0] ?? null
})
const leadingSeries = computed(() => {
    const id = featuredIdBySubtype.value[4]
    return (id ? seriesList.value.find(s => s.id === id) : null) ?? seriesList.value[0] ?? null
})

const featuredOfLink = computed(() => {
    if (featuredIndex.value === null) return null
    return props.featuredGoals[featuredIndex.value]?.oflink ?? null
})

const featuredFlower = computed(() => {
    if (featuredIndex.value === null) return null
    return props.featuredGoals[featuredIndex.value]?.flowers ?? null
})

const featuredMembersChoice = computed(() => {
    if (featuredIndex.value === null) return null
    return props.featuredGoals[featuredIndex.value]?.membersChoice ?? null
})

// Each option votes independently (its own Vote button, expanding in place
// for free-amount products) rather than the tile itself being clickable —
// the tile's own click is reserved for playing/pausing its video, and the two
// can't share one handler without one swallowing the other's taps.
const featuredExpandedOptionId = ref(null)
const featuredVideoPlaying = reactive({})

// Options scroll horizontally (same swipe/fade pattern as
// MembersChoiceProductCard's row) rather than being capped to 3 — a product
// can have up to 5.
const mcFeaturedOptionsRow = ref(null)
const mcFeaturedAtStart = ref(true)
const mcFeaturedAtEnd = ref(false)

function updateMcFeaturedEdges(el) {
    mcFeaturedAtStart.value = el.scrollLeft <= 1
    mcFeaturedAtEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
}

function onMcFeaturedOptionsScroll(event) {
    updateMcFeaturedEdges(event.currentTarget)
}

// The scroll-hint fade must cover only the video thumbnails, not the
// title/description/tally/Vote button beneath them — mirrors
// MembersChoiceProductCard's frameHeight, measured off the actual media
// element's rendered height (not a blanket 100% of the whole tile column,
// which was bleeding the fade down over the controls).
const mcFeaturedFrameHeight = ref(0)
let mcFeaturedMediaResizeObserver = null

function observeMcFeaturedMedia() {
    if (mcFeaturedMediaResizeObserver) mcFeaturedMediaResizeObserver.disconnect()
    const media = mcFeaturedOptionsRow.value?.querySelector('.mc-featured-option__media')
    if (!media) return
    mcFeaturedFrameHeight.value = media.getBoundingClientRect().height
    mcFeaturedMediaResizeObserver = new ResizeObserver(([entry]) => {
        mcFeaturedFrameHeight.value = entry.contentRect.height
    })
    mcFeaturedMediaResizeObserver.observe(media)
}

// One shared celebration burst for the whole banner (see the template
// comment by its usage) — bumps whenever any currently-visible option's
// voteBurst counter changes, reusing the same validity-gated increments
// queueVote()/sendVote() already do rather than re-deriving "was this a
// real vote" here.
//
// Clicking the refresh button (or the auto-rotator) swaps in a different
// product with a completely different set of option ids, which changes this
// joined key too — even though nothing was voted on. Track the product id
// alongside the key so a product SWITCH never counts as a vote, only a real
// change in tallies within the SAME product does.
const featuredBurstTrigger = ref(0)
let featuredBurstProductId = null
watch(
    () => featuredMembersChoice.value?.options.map((o) => voteBurst[o.id] ?? 0).join(',') ?? '',
    (val, oldVal) => {
        const productId = featuredMembersChoice.value?.id ?? null
        if (productId === featuredBurstProductId && val !== oldVal) {
            featuredBurstTrigger.value++
        }
        featuredBurstProductId = productId
    }
)

// Impression tracking (analytics) for the featured banner's options — mirrors
// MembersChoiceProductCard's observeOptionsForImpressions/setOptionMediaRef.
// Refs are collected into a plain Map (not reactive state) via the template's
// :ref callback, then observed once per product change.
const { observeElement: observeFeaturedElement } = useImpressionTracking()
const featuredOptionMediaEls = new Map()

function setFeaturedOptionMediaRef(id, el) {
    if (el) featuredOptionMediaEls.set(id, el)
    else featuredOptionMediaEls.delete(id)
}

function observeFeaturedOptionsForImpressions() {
    const productId = featuredMembersChoice.value?.id
    if (!productId) return
    featuredOptionMediaEls.forEach((el, optionId) => {
        observeFeaturedElement(el, () => trackMcOptionViewed(productId, optionId))
    })
}

watch(featuredIndex, () => {
    featuredExpandedOptionId.value = null
    nextTick(() => {
        if (!mcFeaturedOptionsRow.value) return
        mcFeaturedOptionsRow.value.scrollLeft = 0
        updateMcFeaturedEdges(mcFeaturedOptionsRow.value)
        observeMcFeaturedMedia()
        observeFeaturedOptionsForImpressions()
    })
})

onUnmounted(() => {
    if (mcFeaturedMediaResizeObserver) mcFeaturedMediaResizeObserver.disconnect()
})

// Mirrors MembersChoiceProductCard's toggleVideoPlay: strip the poster-frame
// #t= fragment before real playback, pause any other playing video on the
// page, and unmute on the same tap (a direct gesture, so it works on touch).
function toggleFeaturedVideoPlay(event) {
    const video = event.currentTarget
    if (video.paused) {
        document.querySelectorAll('video').forEach((v) => { if (v !== video && !v.paused) v.pause() })
        if (video.src.includes('#t=')) {
            video.src = video.src.split('#')[0]
            video.load()
        }
        video.muted = false
        video.play()
    } else {
        video.pause()
    }
}

// ─── Section modals ───────────────────────────────────────────────────────
const activeModal  = ref(null)
const sectionUrl   = route(page.props.routePrefix + '.shop.section')
function openModal(config) {
    activeModal.value = { ...config, fetchUrl: sectionUrl }
    track('Star Shop Section Expanded', { section_type: config.type, section_total: config.total })
}
function closeModal() { activeModal.value = null }

// ─── Purchase / contribute ────────────────────────────────────────────────
const purchasing = ref(false)
const purchaseFlash = reactive({ message: '', type: '' })
const membershipUpsell = reactive({ open: false, status: 'expired', intent: '', performerName: '', performerPhoto: '', productId: null, productTitle: '', productKind: '', productStars: null, performerId: null })

/**
 * Product context for the wall funnel: Dismissed and CTA Clicked carry the
 * rejected product too, not just Shown, so the funnel can be segmented by
 * what was rejected without stitching events together on user + timestamp.
 * product_stars and performer_id aren't part of track()'s base, so they
 * ride along here (matching star_shop_trial_teaser.blade.php's payload).
 */
function membershipUpsellTrackProps() {
    return {
        membership_status: membershipUpsell.status,
        intent: membershipUpsell.intent || null,
        source: 'star_shop_page',
        product_stars: membershipUpsell.productStars ?? null,
        performer_id: membershipUpsell.performerId ?? null,
    }
}

/**
 * @param {number|null} stars — the actual amount attempted (a free-form
 * contribution's chosen amount, not a fixed price), passed explicitly by the
 * caller rather than looked up — mirrors star_shop_widget.blade.php's
 * equivalent purchaseItem()/purchaseCart() attribution. Only present when
 * productId is, so a single specific product is attributable if the member
 * upgrades from here (see MembershipUpsellModal.vue's onCtaClick).
 */
function openMembershipUpsell(status, productId = null, stars = null, performerId = null) {
    const mechanic = productId ? (mechanicByProductId.value[productId] ?? '') : ''
    membershipUpsell.intent = mechanic === 'oflink' ? 'discount' : mechanic
    membershipUpsell.performerName = (productId ? performerNameById.value[productId] : '') ?? ''
    membershipUpsell.performerPhoto = (productId ? performerPhotoById.value[productId] : '') ?? ''
    membershipUpsell.productId = productId
    membershipUpsell.productTitle = (productId ? productNameById.value[productId] : '') ?? ''
    membershipUpsell.productKind = mechanic
    membershipUpsell.productStars = stars
    membershipUpsell.performerId = performerId
    membershipUpsell.status = status || 'expired'
    membershipUpsell.open = true
    track('Star Shop Membership Wall Shown', membershipUpsellTrackProps(), productId)
}

function closeMembershipUpsell() {
    membershipUpsell.open = false
    track('Star Shop Membership Wall Dismissed', membershipUpsellTrackProps(), membershipUpsell.productId)
}

function trackMembershipUpsellCta() {
    track('Star Shop Membership Wall CTA Clicked', membershipUpsellTrackProps(), membershipUpsell.productId)
}
const successModal = reactive({ open: false, message: '', discountItemValue: null, mechanic: null, stats: null, interstitialAd: null, interstitialAdSkipReason: null, productId: null, starCost: null, performerId: null })
// Interstitial Ad shown right after the success modal closes (trigger:
// 'post_purchase') — see useStarShopPurchase.js's twin of this, which
// Vault.vue uses; kept in sync here since Index.vue owns its own
// contribute()/successModal instead of the shared composable.
const interstitialAd = reactive({ open: false, creative: null, productId: null, starCost: null, performerId: null })

/**
 * Enriches the bare purchased_product_id with the same product_type/
 * product_name shape every other Star Shop event already carries (see
 * track() above) — so Served/Clicked/Dismissed events are self-sufficient
 * for reporting without a join back to the product table. Also carries the
 * same membership_* fields track() already sends for every other event on
 * this page, from the same analyticsContext prop, plus the performer whose
 * product triggered this ad (performerId is threaded through from
 * contribute()'s own param — same id already sent to the purchase endpoint
 * — via successModal, not re-derived here).
 */
const interstitialAdContext = computed(() => ({
    purchased_product_id: interstitialAd.productId,
    purchased_product_type: interstitialAd.productId ? (mechanicByProductId.value[interstitialAd.productId] ?? null) : null,
    purchased_product_name: interstitialAd.productId ? (productNameById.value[interstitialAd.productId] ?? null) : null,
    purchased_star_cost: interstitialAd.starCost,
    performer_id: interstitialAd.performerId,
    performer_name: interstitialAd.performerId ? (performerNameById.value[interstitialAd.productId] ?? null) : null,
    membership_lifecycle_stage: props.analyticsContext?.membership_lifecycle_stage ?? null,
    membership_term: props.analyticsContext?.membership_term ?? null,
    membership_tier: props.analyticsContext?.membership_tier ?? null,
    is_gold: props.analyticsContext?.is_gold ?? null,
}))

// ─── Gamified stat badges + count-up ──────────────────────────────────────
const BADGE_ICONS = {
    first_purchase: 'sparkle',
    top_purchaser: 'trophy',
    top_fan: 'flame',
    top_percentile: 'trending',
    streak: 'zap',
    stars_milestone: 'brand-star',
    order_milestone: 'trophy',
    stars_spent: 'brand-star',
}

// Only badges whose number grows in a satisfying direction get the count-up
// (a "Top 0% → 2%" count-up would read as getting worse).
const COUNT_UP_TYPES = ['stars_spent', 'stars_milestone']

const heroCountUp = ref(0)
let countUpFrame = null

function startHeroCountUp(target) {
    cancelAnimationFrame(countUpFrame)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        heroCountUp.value = target
        return
    }
    heroCountUp.value = 0
    const startedAt = performance.now()
    const duration = 700
    const step = (now) => {
        const progress = Math.min((now - startedAt) / duration, 1)
        heroCountUp.value = Math.round(target * (1 - Math.pow(1 - progress, 3)))
        if (progress < 1) countUpFrame = requestAnimationFrame(step)
    }
    countUpFrame = requestAnimationFrame(step)
}

watch(() => successModal.open, (open) => {
    if (!open) return
    const heroBadge = successModal.stats?.badges?.[0]
    if (heroBadge && COUNT_UP_TYPES.includes(heroBadge.type)) {
        startHeroCountUp(heroBadge.value)
    }
})

function badgeLabel(badge, value) {
    switch (badge.type) {
        case 'first_purchase': return 'First purchase — welcome to the Star Shop!'
        case 'top_purchaser': return "You're our #1 purchaser"
        case 'top_fan': return badge.rank === 1
            ? `You're the #1 fan of ${badge.performer_name}`
            : `Top ${badge.rank} fan of ${badge.performer_name}`
        case 'top_percentile': return `Top ${value}% of all purchasers`
        case 'streak': return `${value}-day purchase streak`
        case 'stars_milestone': return `${value.toLocaleString()}+ stars spent all-time`
        case 'order_milestone': return `That's purchase #${value}!`
        case 'stars_spent': return `${value.toLocaleString()} stars spent all-time`
        default: return null
    }
}

const statBadges = computed(() => {
    const badges = successModal.stats?.badges ?? []
    return badges
        .map((badge, i) => {
            const hero = i === 0
            const value = hero && COUNT_UP_TYPES.includes(badge.type) ? heroCountUp.value : badge.value
            const label = badgeLabel(badge, value)
            return label ? { type: badge.type, label, icon: BADGE_ICONS[badge.type] ?? 'sparkle', hero } : null
        })
        .filter(Boolean)
})

// Copy inside the success modal varies by what was purchased: direct access unlocks
// an access link, everything else with a claimable value is a discount link/code.
const successCopy = computed(() => {
    const value = successModal.discountItemValue
    if (successModal.mechanic === 'direct_access') {
        return { title: 'Your exclusive clips are ready.', noun: 'access link', eyebrow: 'Your access link', accent: '#FDBA74', theme: 'amber' }
    }
    return isUrl(value)
        ? { title: 'Your link is ready.', noun: 'discount link', eyebrow: 'Your discount link', accent: '#67E8F9', theme: 'cyan' }
        : { title: 'Your code is ready.', noun: 'discount code', eyebrow: 'Your discount code', accent: '#A3E635', theme: 'lime' }
})

function isUrl(value) {
    try { return ['http:', 'https:'].includes(new URL(value).protocol) }
    catch { return false }
}
let flashTimer = null

async function contribute(productId, amount, performerId = null) {
    if (!isUserLoggedIn.value) {
        joinRedirect('star_shop_purchase')
        return
    }
    if (purchasing.value) return
    purchasing.value = true
    purchaseFlash.message = ''

    const purchaseProps = {
        purchase_mode: 'single',
        items_count: 1,
        total_stars: amount,
        product_stars: amount,
        mechanic: mechanicByProductId.value[productId] ?? 'unknown',
    }
    track('Star Shop Purchase Attempted', purchaseProps, productId)

    try {
        const res = await fetch(route('members2.shop.purchase'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
                'Accept': 'application/json',
            },
            body: JSON.stringify({ product_id: productId, stars_to_contribute: amount, performer_id: performerId, source: 'star_shop', interstitial_placement: 'star_shop_success' }),
        })
        const data = await res.json()
        if (data.success) {
            delete customAmounts[productId]
            track('Star Shop Purchase Completed', {
                ...purchaseProps,
                stars_charged: amount,
                stars_committed: amount,
            }, productId)
            successModal.message = data.message
            successModal.discountItemValue = data.discount_item_value ?? null
            successModal.mechanic = mechanicByProductId.value[productId] ?? null
            successModal.stats = data.stats ?? null
            successModal.interstitialAd = data.interstitial_ad ?? null
            successModal.interstitialAdSkipReason = data.interstitial_ad_skip_reason ?? null
            successModal.productId = productId
            successModal.starCost = amount
            successModal.performerId = performerId
            successModal.open = true
            if (!data.discount_item_value) {
                closeModal()
            }
        } else if (data.code === 'membership_required') {
            track('Star Shop Purchase Failed', {
                ...purchaseProps,
                error_reason: 'membership_required',
                error_message: data.message,
            }, productId)
            openMembershipUpsell(data.membership_status, productId, amount, performerId)
        } else if (data.message === 'Insufficient star balance.') {
            track('Star Shop Purchase Failed', {
                ...purchaseProps,
                error_reason: 'insufficient_balance',
                error_message: data.message,
            }, productId)
            // No separate "shown" moment here — the recovery modal opens immediately and
            // unconditionally, so modal_opened is already this surface's funnel entry point.
            openBuyStarsModal('star_shop_product', productId)
        } else {
            track('Star Shop Purchase Failed', {
                ...purchaseProps,
                error_reason: 'other',
                error_message: data.message,
            }, productId)
            purchaseFlash.type = 'error'
            purchaseFlash.message = data.message
            clearTimeout(flashTimer)
            flashTimer = setTimeout(() => { purchaseFlash.message = '' }, 5000)
        }
    } catch {
        track('Star Shop Purchase Failed', {
            ...purchaseProps,
            error_reason: 'network',
            error_message: 'Request failed',
        }, productId)
        purchaseFlash.type = 'error'
        purchaseFlash.message = 'Something went wrong. Please try again.'
        clearTimeout(flashTimer)
        flashTimer = setTimeout(() => { purchaseFlash.message = '' }, 5000)
    } finally {
        purchasing.value = false
    }
}

// ─── Members Choice voting ────────────────────────────────────────────────
// 1-star mode batches rapid clicks client-side: the burst + tally react on every
// click, and one POST carries the accumulated total after VOTE_FLUSH_DELAY of
// no clicking — 20 taps land as a single order of 20 stars.
const VOTE_FLUSH_DELAY = 1500
const localVotes = reactive({})    // optimistic tally bumps per option id
const pendingVotes = reactive({})  // clicks accumulated, awaiting flush
const voteBurst = reactive({})     // per-option CelebrationBurst trigger
const votingOptionId = ref(null)   // in-flight lock for free-amount submits
const voteFlushTimers = {}

function displayVotes(option) {
    return (option.votes ?? 0) + (localVotes[option.id] ?? 0)
}

function displayTotalVotes(product) {
    return product.options.reduce((sum, o) => sum + displayVotes(o), 0)
}

function leadingOptionId(product) {
    let leadId = null
    let best = 0
    for (const o of product.options) {
        const v = displayVotes(o)
        if (v > best) {
            best = v
            leadId = o.id
        }
    }
    return leadId
}

function optionVotePct(product, option) {
    const total = displayTotalVotes(product)
    return total > 0 ? Math.round(displayVotes(option) / total * 100) : 0
}

function fireVoteBurst(optionId) {
    // A nonce, not a boolean toggle: CelebrationBurst spawns one independent
    // burst per change, so rapid repeat votes stack fireworks instead of each
    // click's false->true reset killing the previous burst mid-animation.
    voteBurst[optionId] = (voteBurst[optionId] ?? 0) + 1
}

function queueVote(product, option) {
    if (!isUserLoggedIn.value) {
        joinRedirect('star_shop_vote')
        return
    }
    if (product.expired) return

    fireVoteBurst(option.id)
    localVotes[option.id] = (localVotes[option.id] ?? 0) + 1
    pendingVotes[option.id] = (pendingVotes[option.id] ?? 0) + 1

    clearTimeout(voteFlushTimers[option.id])
    voteFlushTimers[option.id] = setTimeout(() => flushVotes(product, option), VOTE_FLUSH_DELAY)
}

async function flushVotes(product, option, keepalive = false) {
    const amount = pendingVotes[option.id] ?? 0
    if (amount < 1) return
    pendingVotes[option.id] = 0
    clearTimeout(voteFlushTimers[option.id])

    await sendVote(product, option, amount, { optimisticApplied: true, keepalive })
}

function submitFreeVote(product, option, presetAmount = null) {
    if (!isUserLoggedIn.value) {
        joinRedirect('star_shop_vote')
        return
    }
    if (product.expired) return
    const amount = Number(presetAmount ?? customAmounts['opt_' + option.id])
    if (!amount || amount < 1 || votingOptionId.value) return

    votingOptionId.value = option.id
    sendVote(product, option, amount, { optimisticApplied: false })
        .finally(() => { votingOptionId.value = null })
}

async function sendVote(product, option, amount, { optimisticApplied, keepalive = false } = {}) {
    const voteProps = {
        purchase_mode: 'vote',
        items_count: 1,
        total_stars: amount,
        product_stars: amount,
        mechanic: 'members_choice',
        option_id: option.id,
    }
    track('Star Shop Purchase Attempted', voteProps, product.id)

    try {
        const res = await fetch(route('members2.shop.purchase'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
                'Accept': 'application/json',
            },
            keepalive,
            body: JSON.stringify({ product_id: product.id, option_id: option.id, stars_to_contribute: amount, source: 'star_shop' }),
        })
        const data = await res.json()

        if (data.success) {
            if (!optimisticApplied) {
                localVotes[option.id] = (localVotes[option.id] ?? 0) + amount
                fireVoteBurst(option.id)
                delete customAmounts['opt_' + option.id]
            }
            track('Star Shop Purchase Completed', { ...voteProps, stars_charged: amount, stars_committed: amount }, product.id)
            scheduleBalanceRefresh()
        } else {
            if (optimisticApplied) {
                localVotes[option.id] = Math.max(0, (localVotes[option.id] ?? 0) - amount)
            }
            if (data.code === 'membership_required') {
                track('Star Shop Purchase Failed', { ...voteProps, error_reason: 'membership_required', error_message: data.message }, product.id)
                openMembershipUpsell(data.membership_status, product.id, amount, product.performer_id ?? null)
            } else if (data.message === 'Insufficient star balance.') {
                track('Star Shop Purchase Failed', { ...voteProps, error_reason: 'insufficient_balance', error_message: data.message }, product.id)
                openBuyStarsModal('star_shop_product', product.id)
            } else {
                track('Star Shop Purchase Failed', { ...voteProps, error_reason: 'other', error_message: data.message }, product.id)
                purchaseFlash.type = 'error'
                purchaseFlash.message = data.message
                clearTimeout(flashTimer)
                flashTimer = setTimeout(() => { purchaseFlash.message = '' }, 5000)
            }
        }
    } catch {
        if (optimisticApplied) {
            localVotes[option.id] = Math.max(0, (localVotes[option.id] ?? 0) - amount)
        }
        track('Star Shop Purchase Failed', { ...voteProps, error_reason: 'network', error_message: 'Request failed' }, product.id)
        purchaseFlash.type = 'error'
        purchaseFlash.message = 'Something went wrong. Please try again.'
        clearTimeout(flashTimer)
        flashTimer = setTimeout(() => { purchaseFlash.message = '' }, 5000)
    }
}

// Header balance reads page.props.auth — refresh it once the click burst settles
let balanceRefreshTimer = null
function scheduleBalanceRefresh() {
    clearTimeout(balanceRefreshTimer)
    balanceRefreshTimer = setTimeout(() => {
        router.reload({ only: ['auth'] })
    }, 2500)
}

// Don't lose pending 1-star clicks if the user leaves mid-batch
function flushAllPendingVotes() {
    for (const product of props.membersChoice) {
        for (const option of product.options) {
            if ((pendingVotes[option.id] ?? 0) > 0) {
                flushVotes(product, option, true)
            }
        }
    }
}

const sectionIndex = computed(() => {
    const visible = [
        props.vaultClips.length      && 'vaultClips',
        props.membersChoice.length   && 'membersChoice',
        availableOfLinksOfLink.value.length && 'discountOfLink',
        availableOfLinksCoupon.value.length && 'discountCoupon',
        availableOfLinksFansly.value.length && 'discountFansly',
        availableDirectAccess.value.length && 'directAccess',
        remasters.value.length       && 'remaster',
        bounties.value.length        && 'rebook',
        xrQueue.value.length         && 'xr',
        seriesList.value.length      && 'series',
        props.flowers.length         && 'flowers',
    ].filter(Boolean)
    return Object.fromEntries(visible.map((k, i) => [k, String(i + 1).padStart(2, '0')]))
})

const hasProducts = computed(() =>
    props.communityGoals.length > 0 ||
    props.membersChoice.length > 0 ||
    props.ofLinks.length > 0 ||
    props.directAccess.length > 0 ||
    props.vaultClips.length > 0 ||
    props.flowers.length > 0
)

const xrLead = computed(() => xrQueue.value.length >= 2 ? xrQueue.value[0].stars - xrQueue.value[1].stars : 0)

function packHeight(i) {
    const n = props.starPacks.length
    const minH = 190, maxH = 460
    return n <= 1 ? maxH : Math.round(minH + (maxH - minH) * (i / (n - 1)))
}


const customAmounts = reactive({})
const buyStarsModalOpen = ref(false)
const tosAccepted = ref(false)
const tosShake = ref(false)
const pendingProductContext = ref(null)
const buyStarsModalSource = ref('unknown')
const buyStarsModalProductId = ref(null)

const buyStarsModalOpenedAt = ref(null)
const buyStarsModalPackSelected = ref(false)

function joinRedirect(source) {
    track('Star Shop Guest Join Redirected', { redirect_source: source })
    window.location.href = route('www.join.signUp', {custom6: source})
}

function openBuyStarsModal(source = 'unknown', productId = null) {
    buyStarsModalOpen.value = true
    pendingProductContext.value = productId
        ? `${mechanicByProductId.value[productId] ?? 'unknown'}:${productId}`
        : null
    // Bounce-events bucket by entry point (e.g. 'star_shop_product'), not per-product —
    // psc3 (set in handleBuyStarPack) stays per-product for revenue attribution; the
    // server normalizes it back to this same bucket when recording purchase_confirmed.
    buyStarsModalSource.value = source
    buyStarsModalProductId.value = productId
    buyStarsModalOpenedAt.value = Date.now()
    buyStarsModalPackSelected.value = false
    track('Star Shop Buy Stars Modal Opened', {
        origin: source,
        origin_trigger: source === 'star_shop_product' ? 'insufficient_balance' : 'direct',
    }, productId)
    trackStarShopBounceEvent('modal_opened', { source, location: 'star_shop_page', product_id: productId })
}

function closeBuyStarsModal(method) {
    track('Star Shop Buy Stars Modal Closed', {
        origin: buyStarsModalSource.value,
        origin_trigger: buyStarsModalSource.value === 'star_shop_product' ? 'insufficient_balance' : 'direct',
        close_method: typeof method === 'string' ? method : 'x',
        open_duration_ms: buyStarsModalOpenedAt.value ? Date.now() - buyStarsModalOpenedAt.value : null,
        pack_selected: buyStarsModalPackSelected.value,
    }, buyStarsModalProductId.value)
    trackStarShopBounceEvent('modal_closed', { source: buyStarsModalSource.value, location: 'star_shop_page', product_id: buyStarsModalProductId.value })
    buyStarsModalOpen.value = false
}

function handleModalPackSelected(pack) {
    buyStarsModalPackSelected.value = true
    track('Star Shop Star Pack Selected', {
        stars: pack.stars,
        price: pack.price,
        label: pack.label,
        origin: buyStarsModalSource.value,
    }, buyStarsModalProductId.value)
}

function handleModalBuyStarPack(pack) {
    track('Star Shop Star Pack Purchase Attempted', {
        stars: pack.stars,
        price: pack.price,
        label: pack.label,
        trigger: 'buy_stars_modal',
        origin: buyStarsModalSource.value,
    }, buyStarsModalProductId.value)
    trackStarShopBounceEvent('buy_clicked', { source: buyStarsModalSource.value, location: 'star_shop_page', product_id: buyStarsModalProductId.value })
    handleBuyStarPack(pack)
}

function closeSuccessModal() {
    successModal.open = false
    const ad = successModal.interstitialAd
    const skipReason = successModal.interstitialAdSkipReason
    const productId = successModal.productId
    const starCost = successModal.starCost
    const performerId = successModal.performerId
    successModal.discountItemValue = null
    successModal.mechanic = null
    successModal.stats = null
    successModal.interstitialAd = null
    successModal.interstitialAdSkipReason = null
    successModal.productId = null
    successModal.starCost = null
    successModal.performerId = null

    if (ad) {
        interstitialAd.creative = ad
        interstitialAd.productId = productId
        interstitialAd.starCost = starCost
        interstitialAd.performerId = performerId
        interstitialAd.open = true
    } else {
        // Same eventProps() shape InterstitialAdModal.vue uses for
        // Served/Clicked/Dismissed (source/placement/trigger/creative_id/
        // product_line/user_id + context) — skip_reason is the one new
        // field. See useStarShopPurchase.js's twin of this.
        if (skipReason) {
            trackEvent('Interstitial Ad Suppressed', {
                source: 'star_shop_page',
                placement: 'star_shop_success',
                trigger: 'post_purchase',
                creative_id: null,
                product_line: null,
                user_id: page.props.auth?.user?.id ?? null,
                skip_reason: skipReason,
                purchased_product_id: productId,
                purchased_product_type: productId ? (mechanicByProductId.value[productId] ?? null) : null,
                purchased_product_name: productId ? (productNameById.value[productId] ?? null) : null,
                purchased_star_cost: starCost,
                performer_id: performerId,
                performer_name: performerId ? (performerNameById.value[productId] ?? null) : null,
                membership_lifecycle_stage: props.analyticsContext?.membership_lifecycle_stage ?? null,
                membership_term: props.analyticsContext?.membership_term ?? null,
                membership_tier: props.analyticsContext?.membership_tier ?? null,
                is_gold: props.analyticsContext?.is_gold ?? null,
            })
        }
        router.reload()
    }
}

function closeInterstitialAd() {
    interstitialAd.open = false
    interstitialAd.creative = null
    interstitialAd.productId = null
    interstitialAd.starCost = null
    interstitialAd.performerId = null
    router.reload()
}

function copyDiscountValue() {
    if (!successModal.discountItemValue) return
    const text = successModal.discountItemValue
    const fallback = () => {
        const el = document.createElement('textarea')
        el.value = text
        el.style.cssText = 'position:fixed;opacity:0'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
    }
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).catch(fallback)
    } else {
        fallback()
    }
    purchaseFlash.type = 'success'
    purchaseFlash.message = isUrl(successModal.discountItemValue) ? 'Link copied to clipboard!' : 'Code copied to clipboard!'
    clearTimeout(flashTimer)
    flashTimer = setTimeout(() => { purchaseFlash.message = '' }, 3000)
}

function handlePackBuyClick(pack) {
    if (!tosAccepted.value) {
        tosShake.value = true
        setTimeout(() => { tosShake.value = false }, 600)
        return
    }
    track('Star Shop Star Pack Purchase Attempted', {
        stars: pack.stars,
        price: pack.price,
        label: pack.label,
        trigger: 'pack_rail',
        origin: 'direct',
    })

    handleBuyStarPack(pack)
}

function handleBuyStarPack(pack) {
    const returnUrl = window.location.pathname + window.location.search
    const source = pendingProductContext.value
        ? `StarShop:${pendingProductContext.value}`
        : 'StarShop'
    openStarPackPurchaseUrl(pack, source, returnUrl)
}

watch(tosAccepted, (val) => {
    if (val) track('Star Shop TOS Accepted')
})
</script>

<template>
    <div class="shop" data-screen-label="Star Shop">

        <!-- ────────── Page chrome / header ────────── -->
        <HeaderV2
            :isUserLoggedIn="isUserLoggedIn"
            :isGoldUser="isGoldUser"
            :requiresAgeVerification="requiresAgeVerification"
            :routePrefix="page.props.routePrefix"
            :shopBadge="true"

            :hide="{ join: true, signIn: true }"
            :promoteFreeJoin="false"
            @buyStarsClick="openBuyStarsModal('star_shop_header')"
            @goldJoinClick="openBuyStarsModal('star_shop_header_gold_cta')"
            @joinClick="joinRedirect('headerJoinButton_starShop')"
        />

        <BuyStarsModal
            :isOpen="buyStarsModalOpen"
            :userBalance="balance"
            :starPacks="starPacks"
            @close="closeBuyStarsModal"
            @buy="handleModalBuyStarPack"
            @select="handleModalPackSelected"
        />

        <MembershipUpsellModal
            :isOpen="membershipUpsell.open"
            :membershipStatus="membershipUpsell.status"
            :intent="membershipUpsell.intent"
            :performerName="membershipUpsell.performerName"
            :performerPhoto="membershipUpsell.performerPhoto"
            :productId="membershipUpsell.productId"
            :productTitle="membershipUpsell.productTitle"
            :productKind="membershipUpsell.productKind"
            :productStars="membershipUpsell.productStars"
            :performerId="membershipUpsell.performerId"
            source="star_shop_page"
            @close="closeMembershipUpsell"
            @cta="trackMembershipUpsellCta"
        />

        <InterstitialAdModal
            :isOpen="interstitialAd.open"
            :creative="interstitialAd.creative"
            trigger="post_purchase"
            placement="star_shop_success"
            source="star_shop_page"
            :context="interstitialAdContext"
            @close="closeInterstitialAd"
            @cta="closeInterstitialAd"
        />

        <!-- Decorative side gutter stars — desktop only -->
        <div class="shop__deco shop__deco--left" aria-hidden="true">
            <StarMarkSVG
                v-for="i in 16" :key="i" :size="72"
                class="shop__deco-star"
                :style="{ animationDelay: `${(i - 1) * 0.42}s` }"
            />
        </div>
        <div class="shop__deco shop__deco--right" aria-hidden="true">
            <StarMarkSVG
                v-for="i in 16" :key="i" :size="72"
                class="shop__deco-star"
                :style="{ animationDelay: `${(i - 1) * 0.42 + 0.21}s` }"
            />
        </div>

        <!-- Error toast -->
        <transition name="flash">
            <div v-if="purchaseFlash.message" class="purchase-toast purchase-toast--error">
                {{ purchaseFlash.message }}
            </div>
        </transition>

        <!-- Purchase success modal -->
        <transition name="modal-fade">
            <div v-if="successModal.open" class="success-backdrop" @click.self="closeSuccessModal">
                <div class="success-modal">
                    <CelebrationBurst :active="successModal.open" />
                    <div class="success-modal__icon">
                        <StarMarkSVG :size="28" />
                    </div>

                    <template v-if="successModal.discountItemValue">
                        <div class="success-modal__title">{{ successCopy.title }}</div>
                        <div v-if="statBadges.length" class="success-modal__badges">
                            <div v-for="b in statBadges" :key="b.type" class="stat-badge" :class="{ 'stat-badge--hero': b.hero }">
                                <StarMarkSVG v-if="b.icon === 'brand-star'" :size="12" />
                                <StarIcon v-else :name="b.icon" :size="12" />
                                <span>{{ b.label }}</span>
                            </div>
                        </div>
                        <p class="success-modal__message">Unlocked! Your {{ successCopy.noun }} is below. We've also emailed it to you so you'll always have it handy. You can review all your unlocks in your <a :href="route('members2.account.storePurchases')" class="success-modal__link">purchase history</a>.</p>
                        <div class="eyebrow" :style="{ color: successCopy.accent }" style="margin-bottom:8px;text-align:left">{{ successCopy.eyebrow }}</div>
                        <template v-if="isUrl(successModal.discountItemValue)">
                            <a :href="successModal.discountItemValue" target="_blank" rel="noopener" class="of-reveal__link" :class="`of-reveal__link--${successCopy.theme}`">{{ successModal.discountItemValue }}</a>
                        </template>
                        <template v-else>
                            <div class="of-reveal__code" :class="`of-reveal__code--${successCopy.theme}`">{{ successModal.discountItemValue }}</div>
                        </template>
                        <div style="display:flex;gap:8px;margin-top:20px">
                            <button class="btn" :class="`btn--${successCopy.theme}`" style="flex:1" @click="copyDiscountValue">{{ isUrl(successModal.discountItemValue) ? 'Copy link' : 'Copy code' }}</button>
                            <button class="btn btn--ghost" style="flex:1" @click="closeSuccessModal">Done</button>
                        </div>
                    </template>
                    <template v-else>
                        <div class="success-modal__title">Thank you!</div>
                        <div v-if="statBadges.length" class="success-modal__badges">
                            <div v-for="b in statBadges" :key="b.type" class="stat-badge" :class="{ 'stat-badge--hero': b.hero }">
                                <StarMarkSVG v-if="b.icon === 'brand-star'" :size="12" />
                                <StarIcon v-else :name="b.icon" :size="12" />
                                <span>{{ b.label }}</span>
                            </div>
                        </div>
                        <p class="success-modal__message">Unlocked! We've sent a confirmation to your email — keep an eye on your inbox. You can also view all your unlocks in your <a :href="route('members2.account.storePurchases')" class="success-modal__link">purchase history</a>.</p>
                        <button class="btn btn--gold success-modal__cta" @click="closeSuccessModal">Done</button>
                    </template>
                </div>
            </div>
        </transition>

        <template v-if="hasProducts">

        <!-- ────────── HERO ────────── -->
        <section class="hero">
            <div class="hero__glow" />
            <div class="hero__star hero__star--a"><StarMarkSVG :size="36" /></div>
            <div class="hero__star hero__star--b"><StarMarkSVG :size="20" /></div>
            <div class="hero__star hero__star--c"><StarMarkSVG :size="14" /></div>
            <div class="hero__star hero__star--d"><StarMarkSVG :size="28" /></div>
            <div class="hero__star hero__star--e"><StarMarkSVG :size="10" /></div>
            <div class="hero__star hero__star--f"><StarMarkSVG :size="18" /></div>
            <div class="hero__star hero__star--g"><StarMarkSVG :size="8" /></div>

            <div class="hero__grid">
                <div>
                    <div class="eyebrow eyebrow--pill">
                        <StarIcon name="sparkle" :size="11" :stroke-width="2.5" /> Star Shop
                    </div>
                    <h1 class="hero__h1">Spend stars on <span class="grad">what you actually want</span> next.</h1>
                    <p class="hero__sub">{{ heroSub }}</p>
                </div>

                <!-- Logged-in: balance -->
                <aside v-if="isUserLoggedIn" class="balance-card">
                    <div class="eyebrow eyebrow--mute">Your balance</div>
                    <div class="balance-card__row">
                        <span class="balance-card__star-wrap">
                            <StarMarkSVG :size="32" />
                        </span>
                        <span class="balance-card__num">{{ balance.toLocaleString() }}</span>
                        <span class="balance-card__lbl">stars</span>
                    </div>
                    <div class="balance-card__sub">Spend across any mechanic · stars expire 1 year after purchase</div>
                    <div class="balance-card__actions">
                        <button class="btn btn--blue" @click="openBuyStarsModal('star_shop_balance_card')">Buy more stars</button>
                        <a :href="route('members2.account.storePurchases')" class="btn btn--ghost">History</a>
                    </div>
                </aside>

                <!-- Guest: join / sign-in prompt -->
                <aside v-else class="balance-card balance-card--guest">
                    <div class="balance-card__guest-icon">
                        <StarMarkSVG :size="36" />
                    </div>
                    <div class="balance-card__guest-h">Spend stars on what gets made next.</div>
                    <p class="balance-card__guest-sub">Join to get stars with your membership and back any mechanic on this page.</p>
                    <div class="balance-card__actions">
                        <a :href="route('www.join.signUp', {custom6: 'starShopJoin'})" class="btn btn--blue">Join now</a>
                        <a :href="route('members2.auth.login')" class="btn btn--ghost">Sign in</a>
                    </div>
                </aside>
            </div>
        </section>

        <!-- ────────── FEATURED SLOT ────────── -->
        <section v-if="featuredKey" class="featured" :class="`featured--${featuredKey}`">
            <div class="featured__inner">
                <img v-if="currentFeaturedImage"
                     :src="currentFeaturedImage"
                     class="featured__bg-img"
                     aria-hidden="true" />
                <div class="featured__overlay" />

                <button
                    v-if="props.featuredGoals.length > 1"
                    class="featured__refresh"
                    :class="{ 'featured__refresh--over-image': !!currentFeaturedImage }"
                    title="Show a different featured product"
                    @click="refreshFeatured"
                >
                    <RefreshCw :size="15" :stroke-width="2.2" />
                </button>

                <div class="featured__copy">
                    <!-- RE-BOOK -->
                    <template v-if="featuredKey === 'rebook'">
                        <template v-if="featuredBounty">
                            <div class="status status--rose">
                                <StarIcon name="flame" :size="11" :stroke-width="2.5" />
                                {{ featuredBounty.momentum }} · {{ (featuredBounty.goal - featuredBounty.progress).toLocaleString() }} from triggering
                            </div>
                            <div class="eyebrow">Re-Book Bounty<template v-if="featuredBounty.lastSeen"> · Last seen {{ featuredBounty.lastSeen }}</template></div>
                            <h2 class="featured__h2">Bring back <span style="color:#FB7185">{{ featuredBounty.name }}</span>.</h2>
                            <p class="featured__sub">
                                <template v-if="featuredBounty.backers === 0">
                                    {{ featuredBounty.note ? featuredBounty.note + ' ' : '' }}Be the first to back this. Once the bounty hits {{ featuredBounty.goal.toLocaleString() }} stars, producers reach out to book her.
                                </template>
                                <template v-else>
                                    {{ featuredBounty.note ? featuredBounty.note + ' ' : '' }}{{ featuredBounty.backers }} {{ featuredBounty.backers === 1 ? 'member has' : 'members have' }} funded {{ featuredBounty.progress.toLocaleString() }} stars to bring her back.
                                    {{ (featuredBounty.goal - featuredBounty.progress).toLocaleString() }} more and producers reach out to book.
                                </template>
                            </p>
                            <FeaturedProgress :pct="featuredBounty.pct" :n="featuredBounty.progress" :g="featuredBounty.goal" tone="rose" />
                            <div class="featured__actions">
                                <template v-if="featuredBounty.starCost > 0">
                                    <button class="btn btn--rose-soft" :class="{ 'btn--loading': purchasing }" :disabled="purchasing"
                                        @click="contribute(featuredBounty.id, featuredBounty.starCost)">
                                        <span v-if="purchasing" class="btn-spinner" />
                                        <StarMarkSVG v-else :size="14" />
                                        {{ purchasing ? 'Processing…' : 'Back with ' + featuredBounty.starCost.toLocaleString() + ' stars' }}
                                    </button>
                                </template>
                                <template v-else>
                                    <div class="pick-row">
                                        <button v-for="amt in [50,200,500]" :key="amt" class="btn btn--rose-soft" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(featuredBounty.id, amt)">
                                            <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                            <template v-else><StarMarkSVG :size="10" /> {{ amt }}</template>
                                        </button>
                                        <div class="amount-field">
                                            <StarMarkSVG class="amount-field__icon" :size="11" />
                                            <input v-model.number="customAmounts[featuredBounty.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[featuredBounty.id] && contribute(featuredBounty.id, customAmounts[featuredBounty.id])" />
                                        </div>
                                        <button class="btn btn--rose-soft amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[featuredBounty.id]" @click="contribute(featuredBounty.id, customAmounts[featuredBounty.id])">
                                            <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                            <template v-else>→</template>
                                        </button>
                                    </div>
                                </template>
                                <a v-if="featuredBounty.performerSlug" :href="route(page.props.routePrefix + '.performer.show', { slug: featuredBounty.performerSlug })" class="btn btn--ghost">See her last scenes →</a>
                            </div>
                        </template>
                    </template>

                    <!-- REMASTER -->
                    <template v-else-if="featuredKey === 'remaster'">
                        <template v-if="featuredRemaster">
                            <div class="status status--gold">
                                <StarIcon name="flame" :size="11" :stroke-width="2.5" />
                                {{ (featuredRemaster.goal - featuredRemaster.progress).toLocaleString() }} stars from funded
                            </div>
                            <div class="eyebrow">Remaster Bids<template v-if="featuredRemaster.series || featuredRemaster.year"> · {{ featuredRemaster.series }}<template v-if="featuredRemaster.year"> ({{ featuredRemaster.year }})</template></template></div>
                            <h2 class="featured__h2">Bring back <span style="color:#FCD34D">{{ featuredRemaster.title }}</span> in 4K or VR.</h2>
                            <p class="featured__sub">
                                <template v-if="featuredRemaster.backers === 0">
                                    {{ featuredRemaster.note ? featuredRemaster.note + ' ' : '' }}Be the first to fund this remaster. {{ featuredRemaster.goal.toLocaleString() }} stars gets it into production.
                                </template>
                                <template v-else>
                                    {{ featuredRemaster.note ? featuredRemaster.note + ' ' : '' }}{{ featuredRemaster.backers }} {{ featuredRemaster.backers === 1 ? 'member is' : 'members are' }} funding the remaster.
                                    {{ (featuredRemaster.goal - featuredRemaster.progress).toLocaleString() }} more stars and it ships.
                                </template>
                            </p>
                            <FeaturedProgress :pct="featuredRemaster.pct" :n="featuredRemaster.progress" :g="featuredRemaster.goal" tone="gold" />
                            <div class="featured__actions">
                                <template v-if="featuredRemaster.starCost > 0">
                                    <button class="btn btn--gold" :class="{ 'btn--loading': purchasing }" :disabled="purchasing"
                                        @click="contribute(featuredRemaster.id, featuredRemaster.starCost)">
                                        <span v-if="purchasing" class="btn-spinner" />
                                        <StarMarkSVG v-else :size="14" />
                                        {{ purchasing ? 'Processing…' : 'Fund ' + featuredRemaster.starCost.toLocaleString() + ' stars' }}
                                    </button>
                                </template>
                                <template v-else>
                                    <div class="pick-row">
                                        <button v-for="amt in [100,250,500]" :key="amt" class="btn btn--gold" style="font-size:11px;padding:0 4px;height:36px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(featuredRemaster.id, amt)">
                                            <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                            <template v-else><StarMarkSVG :size="9" /> {{ amt }}</template>
                                        </button>
                                        <div class="amount-field">
                                            <StarMarkSVG class="amount-field__icon" :size="11" />
                                            <input v-model.number="customAmounts[featuredRemaster.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[featuredRemaster.id] && contribute(featuredRemaster.id, customAmounts[featuredRemaster.id])" />
                                        </div>
                                        <button class="btn btn--gold amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[featuredRemaster.id]" @click="contribute(featuredRemaster.id, customAmounts[featuredRemaster.id])">
                                            <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                            <template v-else>→</template>
                                        </button>
                                    </div>
                                </template>
                                <a v-if="featuredRemaster.sceneSlug" :href="route(page.props.routePrefix + '.scene.show', { slug: featuredRemaster.sceneSlug })" class="btn btn--ghost">Watch current scene →</a>
                            </div>
                        </template>
                    </template>

                    <!-- XR -->
                    <template v-else-if="featuredKey === 'xr'">
                        <template v-if="topXR">
                            <div class="status status--violet">
                                <StarIcon name="trending" :size="11" :stroke-width="2.5" />
                                Stars pool toward the goal · hit it and the shoot gets scheduled
                            </div>
                            <div class="eyebrow">Take Her to VR / AR</div>
                            <h2 class="featured__h2">Back her into <span style="color:#C4B5FD">VR / AR</span>.</h2>
                            <p class="featured__sub">
                                {{ topXR.note ? topXR.note + ' ' : '' }}Stars from the community pool toward a goal. Once it's hit, the immersive shoot gets scheduled.
                            </p>
                            <p class="featured__sub" style="margin-top:0">{{ topXR.name }}</p>
                            <FeaturedProgress v-if="topXR.goal" :pct="topXR.pct" :n="topXR.stars" :g="topXR.goal" tone="violet" />
                            <div class="featured__actions">
                                <template v-if="topXR.starCost > 0">
                                    <button class="btn btn--violet" :class="{ 'btn--loading': purchasing }" :disabled="purchasing"
                                        @click="contribute(topXR.id, topXR.starCost)">
                                        <span v-if="purchasing" class="btn-spinner" />
                                        <StarMarkSVG v-else :size="14" />
                                        {{ purchasing ? 'Processing…' : 'Back ' + topXR.name + ' · ' + topXR.starCost.toLocaleString() + ' stars' }}
                                    </button>
                                </template>
                                <template v-else>
                                    <div class="pick-row">
                                        <button v-for="amt in [100,250,500]" :key="amt" class="btn btn--violet-soft" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(topXR.id, amt)">
                                            <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                            <template v-else><StarMarkSVG :size="10" /> {{ amt }}</template>
                                        </button>
                                        <div class="amount-field">
                                            <StarMarkSVG class="amount-field__icon" :size="11" />
                                            <input v-model.number="customAmounts[topXR.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[topXR.id] && contribute(topXR.id, customAmounts[topXR.id])" />
                                        </div>
                                        <button class="btn btn--violet-soft amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[topXR.id]" @click="contribute(topXR.id, customAmounts[topXR.id])">
                                            <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                            <template v-else>→</template>
                                        </button>
                                    </div>
                                </template>
                            </div>
                        </template>
                    </template>

                    <!-- SERIES -->
                    <template v-else-if="featuredKey === 'series'">
                        <template v-if="leadingSeries">
                            <div class="status status--blue">
                                <StarMarkSVG :size="11" />
                                Stars pool toward the goal · hit it and new episodes get greenlit
                            </div>
                            <div class="eyebrow">Series Resurrection · {{ leadingSeries.dormant }}</div>
                            <h2 class="featured__h2">Revive<br/><span style="color:#4D94FF">{{ leadingSeries.title }}</span>.</h2>
                            <p class="featured__sub">
                                {{ leadingSeries.note ? leadingSeries.note + ' ' : '' }}{{ leadingSeries.votes.toLocaleString() }} stars contributed so far. Once the goal is hit, new episodes get the greenlight.
                            </p>
                            <FeaturedProgress :pct="leadingSeries.pct" :n="leadingSeries.votes" :g="leadingSeries.triggerAt" tone="blue" label="stars" />
                            <div class="featured__actions">
                                <template v-if="leadingSeries.starCost > 0">
                                    <button class="btn btn--blue" :class="{ 'btn--loading': purchasing }" :disabled="purchasing"
                                        @click="contribute(leadingSeries.id, leadingSeries.starCost)">
                                        <span v-if="purchasing" class="btn-spinner" />
                                        <StarMarkSVG v-else :size="14" />
                                        {{ purchasing ? 'Processing…' : 'Fund · ' + leadingSeries.starCost.toLocaleString() + ' stars' }}
                                    </button>
                                </template>
                                <template v-else>
                                    <div class="pick-row">
                                        <button v-for="amt in [50,100,250]" :key="amt" class="btn btn--blue" style="font-size:11px;padding:0 4px;height:36px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(leadingSeries.id, amt)">
                                            <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                            <template v-else><StarMarkSVG :size="9" /> {{ amt }}</template>
                                        </button>
                                        <div class="amount-field">
                                            <StarMarkSVG class="amount-field__icon" :size="11" />
                                            <input v-model.number="customAmounts[leadingSeries.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[leadingSeries.id] && contribute(leadingSeries.id, customAmounts[leadingSeries.id])" />
                                        </div>
                                        <button class="btn btn--blue amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[leadingSeries.id]" @click="contribute(leadingSeries.id, customAmounts[leadingSeries.id])">
                                            <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                            <template v-else>→</template>
                                        </button>
                                    </div>
                                </template>
                                <button class="btn btn--ghost">See the full series catalog →</button>
                            </div>
                        </template>
                    </template>

                    <!-- DISCOUNT (OF Link or Coupon) -->
                    <template v-else-if="featuredKey === 'oflink'">
                        <template v-if="featuredOfLink">
                            <div class="status" :class="featuredOfLink.discount_subtype === 2 ? 'status--lime' : 'status--cyan'">
                                <StarIcon name="zap" :size="11" :stroke-width="2.5" />
                                {{ featuredOfLink.available }} item{{ featuredOfLink.available === 1 ? '' : 's' }} remaining
                            </div>
                            <div class="eyebrow">{{ featuredOfLink.discount_subtype === 1 ? 'OF Discount Link' : featuredOfLink.discount_subtype === 3 ? 'Fansly Discount Link' : 'Discount Coupon' }}</div>
                            <h2 class="featured__h2">
                                <template v-if="featuredOfLink.discount_subtype === 1">Unlock <span style="color:#67E8F9">{{ featuredOfLink.name }}</span>'s OnlyFans.</template>
                                <template v-else-if="featuredOfLink.discount_subtype === 3">Unlock <span style="color:#67E8F9">{{ featuredOfLink.name }}</span>'s Fansly.</template>
                                <template v-else>Save with <span style="color:#A3E635">{{ featuredOfLink.name }}</span>.</template>
                            </h2>
                            <p class="featured__sub">
                                <template v-if="featuredOfLink.product_description">{{ featuredOfLink.product_description }}</template>
                                <template v-else-if="featuredOfLink.discount_subtype === 1">Unlock a discount link for this performer's OnlyFans, straight from the shop. Your stars convert into a private link the moment you buy.</template>
                                <template v-else-if="featuredOfLink.discount_subtype === 3">Unlock a discount link for this performer's Fansly, straight from the shop. Your stars convert into a private link the moment you buy.</template>
                                <template v-else>Redeem an exclusive discount code, straight from the shop. Your stars convert into a private code the moment you buy.</template>
                            </p>
                            <div v-if="featuredOfLink.off" class="of-offer" style="margin-bottom:16px" :style="featuredOfLink.discount_subtype === 2 ? { '--offer-accent': '#A3E635' } : null">
                                <template v-if="featuredOfLink.off_type === 'months'">
                                    <span class="of-offer__num">{{ featuredOfLink.off }}</span>
                                    <span class="of-offer__lbl">free month{{ featuredOfLink.off === 1 ? '' : 's' }}</span>
                                </template>
                                <template v-else-if="featuredOfLink.off_type === 'days'">
                                    <span class="of-offer__num">{{ featuredOfLink.off }}</span>
                                    <span class="of-offer__lbl">free day{{ featuredOfLink.off === 1 ? '' : 's' }}</span>
                                </template>
                                <template v-else>
                                    <span class="of-offer__num">{{ featuredOfLink.off_type === '$' ? '$' : '' }}{{ featuredOfLink.off }}{{ featuredOfLink.off_type === '%' ? '%' : '' }}</span>
                                    <span class="of-offer__lbl">off<template v-if="featuredOfLink.period"><br/>{{ featuredOfLink.period }}</template></span>
                                </template>
                            </div>
                            <div class="featured__actions">
                                <button class="btn" :class="[featuredOfLink.discount_subtype === 2 ? 'btn--lime' : 'btn--cyan', { 'btn--loading': purchasing }]" :disabled="!featuredOfLink.available || purchasing"
                                    @click="featuredOfLink.available && contribute(featuredOfLink.product_id, featuredOfLink.price, featuredOfLink.performer_id)">
                                    <span v-if="purchasing" class="btn-spinner" />
                                    <StarMarkSVG v-else :size="14" />
                                    {{ purchasing ? 'Processing…' : featuredOfLink.available ? (featuredOfLink.discount_subtype === 2 ? 'Get coupon' : 'Unlock link') + ' · ' + featuredOfLink.price.toLocaleString() + ' stars' : 'Unavailable' }}
                                </button>
                            </div>
                        </template>
                    </template>

                    <!-- FLOWERS -->
                    <template v-else-if="featuredKey === 'flowers'">
                        <template v-if="featuredFlower">
                            <div class="status status--pink">
                                <StarIcon name="heart" :size="11" :stroke-width="2.5" />
                                Real flowers delivered to set
                            </div>
                            <div class="eyebrow">Bucket of Flowers</div>
                            <h2 class="featured__h2">Send flowers to <span style="color:#F9A8D4">{{ featuredFlower.name }}</span>.</h2>
                            <p class="featured__sub">
                                {{ featuredFlower.product_description || 'Stars become something she can actually hold. We deliver fresh flowers to set with your note.' }}
                            </p>
                            <div class="featured__actions">
                                <button class="btn btn--pink-soft" :class="{ 'btn--loading': purchasing }" :disabled="purchasing"
                                    @click="contribute(featuredFlower.product_id, featuredFlower.price, featuredFlower.performer_id)">
                                    <span v-if="purchasing" class="btn-spinner" />
                                    <StarMarkSVG v-else :size="14" />
                                    {{ purchasing ? 'Processing…' : 'Send flowers · ' + featuredFlower.price.toLocaleString() + ' stars' }}
                                </button>
                            </div>
                        </template>
                    </template>

                    <!-- MEMBER'S CHOICE -->
                    <template v-else-if="featuredKey === 'members_choice'">
                        <template v-if="featuredMembersChoice">
                            <div class="status" :class="featuredMembersChoice.expired ? 'status--blue' : 'status--emerald'">
                                <StarIcon :name="featuredMembersChoice.expired ? 'trophy' : 'flame'" :size="11" :stroke-width="2.5" />
                                <template v-if="featuredMembersChoice.expired">Voting closed</template>
                                <template v-else>{{ featuredMembersChoice.total_voters.toLocaleString() }} {{ featuredMembersChoice.total_voters === 1 ? 'member has' : 'members have' }} voted</template>
                            </div>
                            <div class="eyebrow">Member's Choice<template v-if="featuredMembersChoice.performer_name"> · {{ featuredMembersChoice.performer_name }}</template></div>
                            <h2 class="featured__h2">Vote for <span style="color:#34D399">{{ featuredMembersChoice.name }}</span>.</h2>
                            <p class="featured__sub">
                                {{ featuredMembersChoice.description || 'Cast your stars and help decide what she does next.' }}
                            </p>

                            <div class="mc-featured-options-wrap">
                                <div class="mc-featured-options-frame" :class="{ 'mc-featured-options-frame--at-start': mcFeaturedAtStart, 'mc-featured-options-frame--at-end': mcFeaturedAtEnd }"
                                    :style="{ height: mcFeaturedFrameHeight + 'px' }">
                                    <span class="mc-featured-options-fade mc-featured-options-fade--left"></span>
                                    <span class="mc-featured-options-fade mc-featured-options-fade--right"></span>
                                </div>
                                <div class="mc-featured-options" ref="mcFeaturedOptionsRow" @scroll="onMcFeaturedOptionsScroll"
                                    :class="{ 'mc-featured-options--scrollable': featuredMembersChoice.options.length > 3 }">
                                    <div v-for="o in featuredMembersChoice.options" :key="o.id"
                                        class="mc-featured-option"
                                        :class="{ 'mc-featured-option--leading': !featuredMembersChoice.expired && o.id === leadingOptionId(featuredMembersChoice), 'mc-featured-option--winner': featuredMembersChoice.expired && o.id === featuredMembersChoice.winner_option_id }"
                                    >
                                        <div class="mc-featured-option__media" :ref="(el) => setFeaturedOptionMediaRef(o.id, el)">
                                            <!-- No poster image field exists on star_shop_product_options — the
                                                 #t=0.1 media fragment forces the browser to decode/paint that
                                                 frame as a thumbnail without playing back, same trick used by
                                                 MembersChoiceProductCard.vue's posterFrameSrc(). Tapping it plays
                                                 for real (toggleFeaturedVideoPlay) — voting lives in its own
                                                 button below so the two taps never collide. -->
                                            <video v-if="o.media_type === 'video'" :src="o.media_url + '#t=0.1'" muted playsinline preload="auto" disablePictureInPicture
                                                @click="toggleFeaturedVideoPlay"
                                                @play="featuredVideoPlaying[o.id] = true"
                                                @pause="featuredVideoPlaying[o.id] = false"
                                                @ended="featuredVideoPlaying[o.id] = false" />
                                            <img v-else :src="o.media_url" :alt="o.title" loading="lazy" />
                                            <span v-if="o.media_type === 'video' && !featuredVideoPlaying[o.id]" class="mc-featured-option__play">
                                                <Play :size="14" fill="currentColor" :stroke-width="0" />
                                            </span>
                                            <span v-if="featuredMembersChoice.expired && o.id === featuredMembersChoice.winner_option_id" class="mc-ribbon">
                                                <StarIcon name="trophy" :size="10" :stroke-width="2.5" /> Winner
                                            </span>
                                            <span v-else-if="!featuredMembersChoice.expired && o.id === leadingOptionId(featuredMembersChoice)" class="mc-leading">Leading</span>
                                        </div>
                                        <div class="mc-featured-option__body">
                                            <div class="mc-featured-option__text">
                                                <div class="mc-featured-option__title">{{ o.title }}</div>
                                                <p v-if="o.description" class="mc-muted mc-featured-option__desc">{{ o.description }}</p>
                                            </div>
                                            <div class="mc-featured-option__tally">
                                                <span class="mc-featured-option__tally-left">
                                                    <StarMarkSVG :size="11" />
                                                    <span class="mc-featured-option__count">{{ displayVotes(o).toLocaleString() }}</span> stars
                                                    <span class="mc-muted">· {{ o.voters }} voter{{ o.voters === 1 ? '' : 's' }}</span>
                                                </span>
                                                <span class="mc-featured-option__pct">{{ optionVotePct(featuredMembersChoice, o) }}%</span>
                                            </div>
                                            <div class="mc-featured-bar"><div class="mc-featured-bar__fill" :style="{ width: optionVotePct(featuredMembersChoice, o) + '%' }" /></div>

                                            <div v-if="!featuredMembersChoice.expired" class="mc-featured-option__vote">
                                                <button v-if="featuredMembersChoice.format === 'single'" class="mc-featured-vote-btn mc-featured-vote-btn--full"
                                                    :disabled="votingOptionId === o.id"
                                                    @click="queueVote(featuredMembersChoice, o)">
                                                    <StarMarkSVG :size="11" /> Vote · 1 star
                                                </button>
                                                <template v-else>
                                                    <button v-if="featuredExpandedOptionId !== o.id" class="mc-featured-vote-btn mc-featured-vote-btn--full"
                                                        @click="featuredExpandedOptionId = o.id">
                                                        <StarMarkSVG :size="11" /> Vote
                                                    </button>
                                                    <div v-else class="mc-featured-vote-expanded">
                                                        <div class="mc-featured-pick-row">
                                                            <button v-for="amt in [25,50,100]" :key="amt" class="mc-featured-pick-btn"
                                                                :disabled="votingOptionId === o.id"
                                                                @click="submitFreeVote(featuredMembersChoice, o, amt)">
                                                                <StarMarkSVG :size="9" /> {{ amt }}
                                                            </button>
                                                        </div>
                                                        <div class="mc-featured-amount-row">
                                                            <input v-model.number="customAmounts['opt_' + o.id]" type="number" min="1" placeholder="Enter amount…" class="mc-featured-amount-input"
                                                                @keyup.enter="customAmounts['opt_' + o.id] && submitFreeVote(featuredMembersChoice, o, null)" />
                                                            <button class="mc-featured-vote-btn mc-featured-vote-btn--submit"
                                                                :disabled="votingOptionId === o.id || !customAmounts['opt_' + o.id]"
                                                                @click="submitFreeVote(featuredMembersChoice, o, null)">→</button>
                                                        </div>
                                                        <button type="button" class="mc-featured-vote-cancel" @click="featuredExpandedOptionId = null">Cancel</button>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- Rendered as a sibling of the scrolling row, not inside it — a
                                     per-option burst nested in .mc-featured-options (overflow-x:
                                     auto) got its radiating particles clipped at the tile's own
                                     edges. One shared burst here, over the whole wrap, has nothing
                                     clipping it and celebrates any vote in the visible product. -->
                                <div class="mc-featured-burst-anchor"><CelebrationBurst :trigger="featuredBurstTrigger" /></div>
                            </div>
                        </template>
                    </template>

                </div>

            </div>
        </section>

        <!-- ────────── CATALOG HEADER ────────── -->
        <section class="catalog-head" :style="{ paddingTop: sectionGap + 'px' }">
            <div class="eyebrow eyebrow--center" style="color:#FCD34D">What you can do</div>
            <h2 class="display">{{ catalogTitle }}</h2>
            <p class="catalog-head__sub">We're rolling these out as the first round. More on the way.</p>
        </section>

        <!-- ────────── ROWS (mechanics/products) ────────── -->
        <div class="rows" style="gap: 24px">

            <!-- 00 — VAULT CLIPS — leads the catalog when available -->
            <RowShell v-if="vaultClips.length" :index="sectionIndex.vaultClips" accent="#FCD34D" eyebrow="Clips"
                      title="Unlock clips with stars."
                      sub="Behind-the-scenes footage, director's cuts, unreleased clips, and more — spend stars to unlock any clip, yours to keep once claimed.">
                <div class="vault-carousel">
                    <button v-if="vaultClips.length > 1" class="vault-carousel__arrow vault-carousel__arrow--left" :disabled="vcAtStart" aria-label="Previous clip" @click="vcPrev">
                        <ChevronLeft :size="24" :stroke-width="2.5" />
                    </button>

                    <div ref="vcTrack" class="vault-carousel__track" @scroll="onVcTrackScroll">
                        <DirectAccessProductCard
                            v-for="o in vaultClips" :key="o.id"
                            class="vault-carousel__card"
                            :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                            :data-product-id="o.product_id"
                            @contribute="contribute(o.product_id, o.price, o.performer_id)"
                            @share="copyShareLink(o.product_id)"
                            @viewed="trackItemViewed"
                            @download="trackItemDownloaded"
                        />
                    </div>

                    <button v-if="vaultClips.length > 1" class="vault-carousel__arrow vault-carousel__arrow--right" :disabled="vcAtEnd" aria-label="Next clip" @click="vcNext">
                        <ChevronRight :size="24" :stroke-width="2.5" />
                    </button>
                </div>
                <template #footer-action>
                    <a :href="route(page.props.routePrefix + '.shop.vault')" class="row-see-all__link" style="color:#FCD34D; text-decoration:none;">
                        View all clips →
                    </a>
                </template>
            </RowShell>

            <!-- 01 — MEMBER'S CHOICE (voting) -->
            <RowShell v-if="membersChoice.length" :index="sectionIndex.membersChoice" accent="#34D399" eyebrow="Member's Choice"
                      title="You decide what happens next."
                      sub="Vote with stars on the options below. When voting closes, the option with the most stars wins."
                      :footer-note="`${totals.membersChoice} campaign${totals.membersChoice === 1 ? '' : 's'} running · every star counts`">
                <div class="mc-carousel">
                    <div class="mc-carousel__stage">
                        <button v-if="mcProducts.length > 1" class="mc-carousel__arrow mc-carousel__arrow--left" :disabled="mcAtStart" aria-label="Previous vote" @click="mcPrev">
                            <ChevronLeft :size="24" :stroke-width="2.5" />
                        </button>

                        <!-- Native scroll-snap track — touch swipe / trackpad, same
                             markup at every breakpoint. Each card fills the full track
                             width (one product visible at a time, see CSS); arrows/dots
                             are additive nav on top of the same native scroll. -->
                        <div ref="mcTrack" class="mc-carousel__track" @scroll="onMcTrackScroll">
                            <MembersChoiceProductCard
                                v-for="p in mcProducts" :key="p.id"
                                class="mc-carousel__card"
                                :product="p"
                                :local-votes="localVotes"
                                :vote-burst="voteBurst"
                                :custom-amounts="customAmounts"
                                :voting-option-id="votingOptionId"
                                :is-copied="copied === p.id"
                                :balance="balance"
                                @vote="queueVote"
                                @free-vote="submitFreeVote"
                                @share="copyShareLink"
                                @buy-stars="openBuyStarsModal('star_shop_members_choice')"
                                @option-viewed="trackMcOptionViewed"
                                @cta-clicked="trackMcCtaClick" />
                        </div>

                        <button v-if="mcProducts.length > 1" class="mc-carousel__arrow mc-carousel__arrow--right" :disabled="mcAtEnd" aria-label="Next vote" @click="mcNext">
                            <ChevronRight :size="24" :stroke-width="2.5" />
                        </button>
                    </div>
                    <div v-if="mcProducts.length > 1" class="mc-carousel__dots">
                        <button v-for="(p, i) in mcProducts" :key="p.id" class="mc-carousel__dot" :class="{ 'mc-carousel__dot--active': i === mcIndex }"
                                :aria-label="`Go to vote ${i + 1}`" @click="mcGoTo(i)" />
                    </div>
                </div>
                <template v-if="totals.membersChoice > 3" #footer-action>
                    <button class="row-see-all__link" style="color:#34D399" @click="openModal({ type: 'members_choice', total: totals.membersChoice, eyebrow: 'Member\'s Choice', title: `All ${totals.membersChoice} campaigns`, accent: '#34D399', pageSize: 6, columns: 1 })">
                        See all campaigns →
                    </button>
                </template>
            </RowShell>

            <!-- 01 — DISCOUNT OF LINKS -->
            <RowShell v-if="availableOfLinksOfLink.length" :index="sectionIndex.discountOfLink" accent="#67E8F9" eyebrow="OF Discount Links"
                      title="Unlock OnlyFans for less."
                      sub="Each link unlocks a private discount on a performer's OnlyFans page. Limited drops — once claimed, it closes.">
                <div class="grid grid--4">
                    <DiscountProductCard
                        v-for="o in availableOfLinksOfLink.slice(0, 4)" :key="o.id"
                        :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                        :data-product-id="o.product_id"
                        @contribute="contribute(o.product_id, o.price, o.performer_id)"
                        @share="copyShareLink(o.product_id)"
                    />
                </div>
                <template v-if="totals.ofLinksOfLink > 4" #footer-action>
                    <button class="row-see-all__link" style="color:#67E8F9" @click="openModal({ type: 'discounts_oflink', total: totals.ofLinksOfLink, eyebrow: 'OF Discount Links', title: `All ${totals.ofLinksOfLink} OF discount links`, accent: '#67E8F9', pageSize: 12, columns: 4 })">
                        See all {{ totals.ofLinksOfLink }} OF discount links
                    </button>
                </template>
            </RowShell>

            <!-- 02 — DISCOUNT COUPONS -->
            <RowShell v-if="availableOfLinksCoupon.length" :index="sectionIndex.discountCoupon" accent="#A3E635" eyebrow="Discount Coupons"
                      title="Exclusive member coupons."
                      sub="Redeem a coupon code straight from the shop. Each drop is limited — once claimed, it closes.">
                <div class="grid grid--4">
                    <DiscountProductCard
                        v-for="o in availableOfLinksCoupon.slice(0, 4)" :key="o.id"
                        :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                        :data-product-id="o.product_id"
                        @contribute="contribute(o.product_id, o.price, o.performer_id)"
                        @share="copyShareLink(o.product_id)"
                    />
                </div>
                <template v-if="totals.ofLinksCoupon > 4" #footer-action>
                    <button class="row-see-all__link" style="color:#A3E635" @click="openModal({ type: 'discounts_coupon', total: totals.ofLinksCoupon, eyebrow: 'Discount Coupons', title: `All ${totals.ofLinksCoupon} discount coupons`, accent: '#A3E635', pageSize: 12, columns: 4 })">
                        See all {{ totals.ofLinksCoupon }} discount coupons
                    </button>
                </template>
            </RowShell>

            <!-- 03 — FANSLY DISCOUNT LINKS -->
            <RowShell v-if="availableOfLinksFansly.length" :index="sectionIndex.discountFansly" accent="#67E8F9" eyebrow="Fansly Discount Links"
                      title="Unlock Fansly for less."
                      sub="Each link unlocks a private discount on a performer's Fansly page. Limited drops — once claimed, it closes.">
                <div class="grid grid--4">
                    <DiscountProductCard
                        v-for="o in availableOfLinksFansly.slice(0, 4)" :key="o.id"
                        :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                        :data-product-id="o.product_id"
                        @contribute="contribute(o.product_id, o.price, o.performer_id)"
                        @share="copyShareLink(o.product_id)"
                    />
                </div>
                <template v-if="totals.ofLinksFansly > 4" #footer-action>
                    <button class="row-see-all__link" style="color:#67E8F9" @click="openModal({ type: 'discounts_fansly', total: totals.ofLinksFansly, eyebrow: 'Fansly Discount Links', title: `All ${totals.ofLinksFansly} Fansly discount links`, accent: '#67E8F9', pageSize: 12, columns: 4 })">
                        See all {{ totals.ofLinksFansly }} Fansly discount links
                    </button>
                </template>
            </RowShell>

            <!-- DIRECT ACCESS — EXCLUSIVE CLIPS -->
            <RowShell v-if="availableDirectAccess.length" :index="sectionIndex.directAccess" accent="#FDBA74" eyebrow="Direct Access"
                      title="Unlock exclusive clips."
                      sub="Spend stars to unlock a direct link to exclusive clips. Yours to keep once claimed — each drop is limited.">
                <div class="grid grid--4">
                    <DirectAccessProductCard
                        v-for="o in availableDirectAccess.slice(0, 4)" :key="o.id"
                        :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                        :data-product-id="o.product_id"
                        @contribute="contribute(o.product_id, o.price, o.performer_id)"
                        @share="copyShareLink(o.product_id)"
                        @viewed="trackItemViewed"
                        @download="trackItemDownloaded"
                    />
                </div>
                <template #footer-action>
                    <div style="display:flex; align-items:center; gap:20px;">
                        <button v-if="totals.directAccess > 4" class="row-see-all__link" style="color:#FDBA74" @click="openModal({ type: 'direct_access', total: totals.directAccess, eyebrow: 'Direct Access', title: `All ${totals.directAccess} exclusive clip drops`, accent: '#FDBA74', pageSize: 12, columns: 4 })">
                            See all {{ totals.directAccess }} exclusive clip drops
                        </button>
                        <a :href="route(page.props.routePrefix + '.shop.vault')" class="row-see-all__link" style="color:#FDBA74; text-decoration:none;">
                            Browse the clips →
                        </a>
                    </div>
                </template>
            </RowShell>

            <!-- REMASTER -->
            <RowShell v-if="remasters.length" :index="sectionIndex.remaster" accent="#FCD34D" eyebrow="Remaster Bids"
                      title="Restore the classics in 4K or VR."
                      sub="Fund the remaster of older scenes. Stars pool toward a goal — once the target is hit, the scene gets a full 4K HDR or 6K VR remaster."
                      :footer-note="`${remasters.length} scene${remasters.length === 1 ? '' : 's'} currently in the queue`">
                <div class="grid grid--3">
                    <article v-for="r in remasters.slice(0, 3)" :key="r.id" class="card" :data-product-id="r.id">
                        <div class="card__cover" :style="{ background: r.coverIsImage ? 'linear-gradient(135deg,#2a1d0a,#54421d)' : r.cover }">
                            <img v-if="r.coverIsImage" :src="r.cover" :alt="r.title" class="card__cover-img" />
                            <span v-if="!r.goalReached && (r.hot || isClosingSoon(r.daysLeft))" class="chip chip--rose"><StarIcon name="flame" :size="9" :stroke-width="2.5" /> Closing soon</span>
                            <span class="chip chip--mute card__year">{{ r.year }}</span>
                            <div class="card__title-block">
                                <div class="card__eyebrow">{{ r.series }}</div>
                                <div class="card__title">{{ r.title }}</div>
                            </div>
                        </div>
                        <div class="card__body">
                            <div class="card__line">
                                <span class="muted">{{ r.progress.toLocaleString() }} / {{ r.goal.toLocaleString() }}</span>
                                <span style="color:#FCD34D;font-weight:700">{{ r.pct }}%</span>
                            </div>
                            <div class="bar"><div class="bar__fill bar__fill--gold" :style="{ width: r.pct + '%' }" /></div>
                            <div class="card__meta"><span>{{ r.backers }} backers</span><span v-if="!r.goalReached && expiryLabel(r.daysLeft)" class="expiry-label" :class="{ 'expiry-label--urgent': isClosingSoon(r.daysLeft) }">{{ expiryLabel(r.daysLeft) }}</span></div>
                            <div class="card__actions">
                                <template v-if="!r.goalReached">
                                    <template v-if="r.starCost > 0">
                                        <button class="btn btn--gold w-full" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(r.id, r.starCost)">
                                            <span v-if="purchasing" class="btn-spinner" />
                                            <StarMarkSVG v-else :size="11" />
                                            {{ purchasing ? 'Processing…' : 'Fund · ' + r.starCost.toLocaleString() + ' stars' }}
                                        </button>
                                    </template>
                                    <template v-else>
                                        <div class="pick-row">
                                            <button v-for="amt in [100,250,500]" :key="amt" class="btn btn--gold" style="font-size:11px;padding:0 4px;height:36px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(r.id, amt)">
                                                <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                                <template v-else><StarMarkSVG :size="9" /> {{ amt }}</template>
                                            </button>
                                            <div class="amount-field">
                                                <StarMarkSVG class="amount-field__icon" :size="11" />
                                                <input v-model.number="customAmounts[r.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[r.id] && contribute(r.id, customAmounts[r.id])" />
                                            </div>
                                            <button class="btn btn--gold amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[r.id]" @click="contribute(r.id, customAmounts[r.id])">
                                                <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                                <template v-else>→</template>
                                            </button>
                                        </div>
                                    </template>
                                </template>
                                <div v-else class="goal-reached-msg">Goal reached — fulfillment in progress.</div>
                                <ShareStarProductButton :copied="copied === r.id" @share="copyShareLink(r.id)" />
                            </div>
                        </div>
                    </article>
                </div>
            <template v-if="totals.remasters > 3" #footer-action>
                    <button class="row-see-all__link" @click="openModal({ type: 'remasters', total: totals.remasters, eyebrow: 'Remaster Bids', title: `All ${totals.remasters} scenes in the queue`, accent: '#FCD34D', pageSize: 12, columns: 3 })">
                        See all {{ totals.remasters }} remasters →
                    </button>
                </template>
            </RowShell>

            <!-- 02 — RE-BOOK -->
            <RowShell v-if="bounties.length" :index="sectionIndex.rebook" accent="#FB7185" eyebrow="Re-Book Bounty"
                      title="Bring back the performers you miss."
                      sub="Members pool stars into a bounty. When the bounty crosses its trigger, producers reach out to book that performer."
                      :footer-note="`${bounties.length} active ${bounties.length === 1 ? 'bounty' : 'bounties'} · ${bounties.reduce((s, b) => s + b.backers, 0).toLocaleString()} members backing`">
                <div class="grid grid--3">
                    <article v-for="b in bounties.slice(0, 3)" :key="b.id" class="card card--padded" :data-product-id="b.id">
                        <div class="bounty__head">
                            <div class="avatar" :style="{ background: b.avatarTone }">
                                <img v-if="b.photo" :src="b.photo" :alt="b.name" class="avatar__img" />
                                <template v-else>{{ b.initials }}</template>
                            </div>
                            <div style="flex:1;min-width:0">
                                <div class="bounty__name">{{ b.name }}</div>
                                <div class="muted small">{{ b.lastSeen }}</div>
                            </div>
                            <div class="chip chip--green small">{{ b.momentum }}</div>
                        </div>
                        <div class="card__line">
                            <span class="muted">{{ b.progress.toLocaleString() }} / {{ b.goal.toLocaleString() }}</span>
                            <span style="color:#FB7185;font-weight:700">{{ b.pct }}%</span>
                        </div>
                        <div class="bar"><div class="bar__fill bar__fill--rose" :style="{ width: b.pct + '%' }" /></div>
                        <div class="card__meta" style="margin-bottom:12px"><span class="muted small">{{ b.backers }} members funding</span><span v-if="!b.goalReached && expiryLabel(b.daysLeft)" class="expiry-label" :class="{ 'expiry-label--urgent': isClosingSoon(b.daysLeft) }">{{ expiryLabel(b.daysLeft) }}</span></div>
                        <template v-if="!b.goalReached">
                            <template v-if="b.starCost > 0">
                                <button class="btn btn--rose-soft w-full" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(b.id, b.starCost)">
                                    <span v-if="purchasing" class="btn-spinner" />
                                    <StarMarkSVG v-else :size="10" />
                                    {{ purchasing ? 'Processing…' : 'Fund · ' + b.starCost.toLocaleString() + ' stars' }}
                                </button>
                            </template>
                            <template v-else>
                                <div class="pick-row">
                                    <button v-for="amt in [50,200,500]" :key="amt" class="btn btn--rose-soft" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(b.id, amt)">
                                        <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                        <template v-else><StarMarkSVG :size="10" /> {{ amt }}</template>
                                    </button>
                                    <div class="amount-field">
                                        <StarMarkSVG class="amount-field__icon" :size="11" />
                                        <input v-model.number="customAmounts[b.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[b.id] && contribute(b.id, customAmounts[b.id])" />
                                    </div>
                                    <button class="btn btn--rose-soft amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[b.id]" @click="contribute(b.id, customAmounts[b.id])">
                                        <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                        <template v-else>→</template>
                                    </button>
                                </div>
                            </template>
                        </template>
                        <div v-else class="goal-reached-msg">Goal reached — fulfillment in progress.</div>
                        <ShareStarProductButton :copied="copied === b.id" @share="copyShareLink(b.id)" />
                    </article>
                </div>
                <template v-if="totals.bounties > 3" #footer-action>
                    <button class="row-see-all__link" style="color:#FB7185" @click="openModal({ type: 'bounties', total: totals.bounties, eyebrow: 'Re-Book Bounty', title: `All ${totals.bounties} active bounties`, accent: '#FB7185', pageSize: 12, columns: 3 })">
                        See all {{ totals.bounties }} bounties →
                    </button>
                </template>
            </RowShell>

            <!-- 03 — XR -->
            <RowShell v-if="xrQueue.length" :index="sectionIndex.xr" accent="#C4B5FD" eyebrow="Take Her to VR / AR"
                      title="Back a performer into an immersive shoot."
                      sub="Stars from the community pool toward a goal. Once it's hit, the VR / AR shoot gets scheduled — no race, no ranking, just a funded goal."
                      :footer-note="`${xrQueue.length} performer${xrQueue.length === 1 ? '' : 's'} available`">
                <div class="grid grid--3">
                    <article v-for="p in xrQueue.slice(0, 3)" :key="p.id" class="card card--padded" :data-product-id="p.id">
                        <span class="chip chip--xr-label card__leading">{{ p.label }}</span>
                        <div class="bounty__head">
                            <div class="avatar" :style="{ background: p.tone }">
                                <img v-if="p.photo" :src="p.photo" :alt="p.name" class="avatar__img" />
                                <template v-else>{{ p.initials }}</template>
                            </div>
                            <div style="flex:1;min-width:0">
                                <div class="bounty__name">{{ p.name }}</div>
                                <div v-if="p.productName" class="muted small">{{ p.productName }}</div>
                                <div v-if="p.stars > 0" class="muted small"><StarMarkSVG :size="9" style="vertical-align:-1px" /> {{ p.stars.toLocaleString() }} stars contributed</div>
                            </div>
                        </div>
                        <p v-if="p.note" class="muted small" style="margin:0 0 10px">{{ p.note }}</p>
                        <template v-if="p.goal">
                            <div class="card__line">
                                <span class="muted">{{ p.stars.toLocaleString() }} / {{ p.goal.toLocaleString() }}</span>
                                <span style="color:#C4B5FD;font-weight:700">{{ p.pct }}%</span>
                            </div>
                            <div class="bar" style="margin-bottom:10px"><div class="bar__fill bar__fill--violet" :style="{ width: p.pct + '%' }" /></div>
                        </template>
                        <div v-if="p.you" style="font-size:11px;color:#C4B5FD;font-weight:700;margin-bottom:10px">
                            You contributed {{ p.you.toLocaleString() }} <StarMarkSVG :size="10" style="vertical-align:-1px" />
                        </div>
                        <div v-if="!p.goalReached && expiryLabel(p.daysLeft)" class="card__meta" style="margin-bottom:10px"><span class="expiry-label" :class="{ 'expiry-label--urgent': isClosingSoon(p.daysLeft) }">{{ expiryLabel(p.daysLeft) }}</span></div>
                        <template v-if="!p.goalReached">
                            <template v-if="p.starCost > 0">
                                <button class="btn btn--violet w-full" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(p.id, p.starCost)">
                                    <span v-if="purchasing" class="btn-spinner" />
                                    <StarMarkSVG v-else :size="13" />
                                    {{ purchasing ? 'Processing…' : 'Fund · ' + p.starCost.toLocaleString() + ' stars' }}
                                </button>
                            </template>
                            <template v-else>
                                <div class="pick-row">
                                    <button v-for="amt in [100,250,500]" :key="amt" class="btn btn--violet-soft" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(p.id, amt)">
                                        <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                        <template v-else><StarMarkSVG :size="10" /> {{ amt }}</template>
                                    </button>
                                    <div class="amount-field">
                                        <StarMarkSVG class="amount-field__icon" :size="11" />
                                        <input v-model.number="customAmounts[p.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[p.id] && contribute(p.id, customAmounts[p.id])" />
                                    </div>
                                    <button class="btn btn--violet-soft amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[p.id]" @click="contribute(p.id, customAmounts[p.id])">
                                        <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                        <template v-else>→</template>
                                    </button>
                                </div>
                            </template>
                        </template>
                        <div v-else class="goal-reached-msg">Goal reached — fulfillment in progress.</div>
                        <ShareStarProductButton :copied="copied === p.id" @share="copyShareLink(p.id)" />
                    </article>
                </div>
                <template v-if="totals.xr > 3" #footer-action>
                    <button class="row-see-all__link" style="color:#C4B5FD" @click="openModal({ type: 'xr', total: totals.xr, eyebrow: 'Take Her to VR / AR', title: `All ${totals.xr} performers`, accent: '#C4B5FD', pageSize: 12, columns: 3 })">
                        See all {{ totals.xr }} performers →
                    </button>
                </template>
            </RowShell>

            <!-- 04 — SERIES -->
            <RowShell v-if="seriesList.length" :index="sectionIndex.series" accent="#4D94FF" eyebrow="Series Resurrection"
                      title="Revive the series you miss."
                      sub="Stars pool toward a goal for each dormant series. Hit the target and new episodes get the greenlight."
                      :footer-note="`${seriesList.length} series available`">
                <div class="grid grid--3">
                    <article v-for="s in seriesList.slice(0, 3)" :key="s.id" class="card" :data-product-id="s.id">
                        <div class="card__cover card__cover--sm" :style="{ background: s.cover }">
                            <img v-if="s.logo" :src="s.logo" :alt="s.title" class="series-card__logo" />
                            <div class="card__title-block">
                                <div class="card__eyebrow">{{ s.dormant }}</div>
                                <div v-if="!s.logo" class="card__title">{{ s.title }}</div>
                            </div>
                        </div>
                        <div class="card__body">
                            <div class="card__line">
                                <span class="muted">{{ s.votes.toLocaleString() }} / {{ s.triggerAt.toLocaleString() }} stars</span>
                                <span style="color:#4D94FF;font-weight:700">{{ s.pct }}%</span>
                            </div>
                            <div class="bar"><div class="bar__fill bar__fill--blue" :style="{ width: s.pct + '%' }" /></div>
                            <div v-if="!s.goalReached && expiryLabel(s.daysLeft)" class="card__meta" style="margin:6px 0 2px"><span class="expiry-label" :class="{ 'expiry-label--urgent': isClosingSoon(s.daysLeft) }">{{ expiryLabel(s.daysLeft) }}</span></div>
                            <p v-if="s.note" class="muted small" style="margin:8px 0 10px">{{ s.note }}</p>
                            <div class="card__actions">
                                <template v-if="!s.goalReached">
                                    <template v-if="s.starCost > 0">
                                        <button class="btn btn--blue w-full" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(s.id, s.starCost)">
                                            <span v-if="purchasing" class="btn-spinner" />
                                            <StarMarkSVG v-else :size="11" />
                                            {{ purchasing ? 'Processing…' : s.yourVotes > 0 ? 'Fund again · +' + s.starCost.toLocaleString() + ' stars' : 'Fund · ' + s.starCost.toLocaleString() + ' stars' }}
                                        </button>
                                    </template>
                                    <template v-else>
                                        <div class="pick-row">
                                            <button v-for="amt in [50,100,250]" :key="amt" class="btn btn--blue" style="font-size:11px;padding:0 4px;height:36px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(s.id, amt)">
                                                <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                                <template v-else><StarMarkSVG :size="10" /> {{ amt }}</template>
                                            </button>
                                            <div class="amount-field">
                                                <StarMarkSVG class="amount-field__icon" :size="11" />
                                                <input v-model.number="customAmounts[s.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[s.id] && contribute(s.id, customAmounts[s.id])" />
                                            </div>
                                            <button class="btn btn--blue amount-submit" style="font-size:13px;padding:0 12px;height:36px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[s.id]" @click="contribute(s.id, customAmounts[s.id])">
                                                <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                                <template v-else>→</template>
                                            </button>
                                        </div>
                                    </template>
                                </template>
                                <div v-else class="goal-reached-msg">Goal reached — fulfillment in progress.</div>
                                <ShareStarProductButton :copied="copied === s.id" @share="copyShareLink(s.id)" />
                            </div>
                        </div>
                    </article>
                </div>
                <template v-if="totals.series > 3" #footer-action>
                    <button class="row-see-all__link" style="color:#4D94FF" @click="openModal({ type: 'series', total: totals.series, eyebrow: 'Series Resurrection', title: `All ${totals.series} series`, accent: '#4D94FF', pageSize: 12, columns: 3 })">
                        See all {{ totals.series }} series →
                    </button>
                </template>
            </RowShell>

            <!-- BUCKET OF FLOWERS -->
            <RowShell v-if="flowers.length" :index="sectionIndex.flowers" accent="#F9A8D4" eyebrow="Bucket of Flowers"
                      title="Send a real bouquet to a performer."
                      sub="Stars become something she can actually hold. Pick a performer and we deliver fresh flowers to set with your note.">
                <div class="grid grid--4">
                    <article v-for="f in flowers.slice(0, 4)" :key="f.id" class="card card--padded" :data-product-id="f.product_id">
                        <div class="bounty__head">
                            <div class="avatar avatar--sm" style="width:44px;height:44px;font-size:15px;background:linear-gradient(135deg,#831843,#9D174D)">
                                <img v-if="f.photo" :src="f.photo" :alt="f.name" class="avatar__img" />
                                <template v-else>{{ f.initials }}</template>
                            </div>
                            <div style="flex:1;min-width:0">
                                <div class="bounty__name" style="font-size:15px">{{ f.name }}</div>
                                <div class="muted small">{{ f.product_name }}</div>
                            </div>
                        </div>
                        <p v-if="f.product_description" class="muted small" style="margin:0 0 10px">{{ f.product_description }}</p>
                        <button class="btn btn--pink-soft w-full" style="margin-top:12px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(f.product_id, f.price, f.performer_id)">
                            <span v-if="purchasing" class="btn-spinner" />
                            <StarMarkSVG v-else :size="11" />
                            {{ purchasing ? 'Processing…' : 'Send flowers · ' + f.price.toLocaleString() + ' stars' }}
                        </button>
                        <ShareStarProductButton :copied="copied === f.product_id" @share="copyShareLink(f.product_id)" />
                    </article>
                </div>
                <template v-if="totals.flowers > 4" #footer-action>
                    <button class="row-see-all__link" style="color:#F9A8D4" @click="openModal({ type: 'flowers', total: totals.flowers, eyebrow: 'Bucket of Flowers', title: `All ${totals.flowers} performers`, accent: '#F9A8D4', pageSize: 12, columns: 4 })">
                        See all {{ totals.flowers }} performers
                    </button>
                </template>
            </RowShell>
        </div>

        <!-- ────────── STAR PACKS STAIRCASE ────────── -->
        <section id="buy-stars" class="staircase" :style="{ paddingTop: sectionGap + 'px' }">
            <div class="staircase__glow" />
            <div class="staircase__head">
                <div class="eyebrow eyebrow--center" style="color:#FCD34D">Top up</div>
                <h2 class="display">Power up.</h2>
                <p class="catalog-head__sub">More stars, more power. Spend them across any mechanic, anytime — stars expire 1 year after purchase.</p>
            </div>
            <div class="staircase__grid">
                <article v-for="(p, i) in starPacks" :key="i" class="pack"
                         :class="[{ 'pack--popular': p.popular, 'pack--best': p.best }]"
                         :style="{ height: packHeight(i) + 'px' }"
                         @click="handlePackBuyClick(p)">
                    <span v-if="p.popular" class="pack__pill pack__pill--blue">Most popular</span>
                    <span v-if="p.best" class="pack__pill pack__pill--gold">Best value</span>
                    <div>
                        <div class="eyebrow muted">{{ p.label }}</div>
                        <div class="pack__count">
                            <StarMarkSVG :size="Math.min(18 + i * 4, 32)" />
                            <span class="pack__num pack__num--gold" :style="{ fontSize: (22 + i * 4) + 'px' }">{{ p.stars.toLocaleString() }}</span>
                        </div>
                    </div>
                    <div>
                        <div class="pack__price pack__price--muted" :style="{ fontSize: (20 + i * 2) + 'px' }">{{ '$' + p.price.toFixed(2).replace(/\.00$/, '') }}</div>
                        <button
                           class="btn w-full"
                           :class="[p.best ? 'btn--gold' : p.popular ? 'btn--blue' : 'btn--gold-outline', { 'btn--tos-locked': !tosAccepted }]"
                           style="margin-top:12px"
                           >Buy</button>
                    </div>
                </article>
            </div>

            <div class="staircase__tos" :class="{ 'staircase__tos--shake': tosShake }">
                <label class="staircase__tos-label">
                    <input type="checkbox" v-model="tosAccepted" class="staircase__tos-check" />
                    <span>
                        I agree to the
                        <a href="https://www.naughtyamerica.com/terms-of-service.html" target="_blank" rel="noopener" class="staircase__tos-link">Terms of Service</a>
                        — Stars expire 1 year after purchase.
                    </span>
                </label>
            </div>
        </section>

        </template>
        <template v-else>
            <!-- ────────── EMPTY STATE ────────── -->
            <div class="shop-empty">
                <div class="shop-empty__icon">
                    <StarMarkSVG :size="48" />
                </div>
                <h2 class="shop-empty__h">Products are on the way.</h2>
                <p class="shop-empty__sub">We're loading up the next round of mechanics — check back soon to spend your stars.</p>
            </div>
        </template>

        <!-- ────────── MODALS ────────── -->

        <!-- Remasters -->
        <!-- ────────── SHARED "SEE ALL" MODAL ────────── -->
        <ShopAllModal v-if="activeModal" :open="true" @update:open="closeModal"
                      :type="activeModal.type" :total="activeModal.total"
                      :fetch-url="activeModal.fetchUrl"
                      :eyebrow="activeModal.eyebrow" :title="activeModal.title"
                      :accent="activeModal.accent" :page-size="activeModal.pageSize"
                      :columns="activeModal.columns ?? 3">
            <template #default="{ visibleItems }">
                <!-- Remasters -->
                <div v-if="activeModal.type === 'remasters'" class="grid grid--3">
                    <article v-for="r in visibleItems" :key="r.id" class="card">
                        <div class="card__cover" :style="{ background: r.coverIsImage ? 'linear-gradient(135deg,#2a1d0a,#54421d)' : r.cover }">
                            <img v-if="r.coverIsImage" :src="r.cover" :alt="r.title" class="card__cover-img" />
                            <span v-if="!r.goalReached && (r.hot || isClosingSoon(r.daysLeft))" class="chip chip--rose"><StarIcon name="flame" :size="9" :stroke-width="2.5" /> Closing soon</span>
                            <span class="chip chip--mute card__year">{{ r.year }}</span>
                            <div class="card__title-block">
                                <div class="card__eyebrow">{{ r.series }}</div>
                                <div class="card__title">{{ r.title }}</div>
                            </div>
                        </div>
                        <div class="card__body">
                            <div class="card__line">
                                <span class="muted">{{ r.progress.toLocaleString() }} / {{ r.goal.toLocaleString() }}</span>
                                <span style="color:#FCD34D;font-weight:700">{{ r.pct }}%</span>
                            </div>
                            <div class="bar"><div class="bar__fill bar__fill--gold" :style="{ width: r.pct + '%' }" /></div>
                            <div class="card__meta"><span>{{ r.backers }} backers</span></div>
                            <div class="card__actions">
                                <template v-if="!r.goalReached">
                                    <template v-if="r.starCost > 0">
                                        <button class="btn btn--gold w-full" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(r.id, r.starCost)">
                                            <span v-if="purchasing" class="btn-spinner" /><StarMarkSVG v-else :size="11" />
                                            {{ purchasing ? 'Processing…' : 'Fund · ' + r.starCost.toLocaleString() + ' stars' }}
                                        </button>
                                    </template>
                                    <template v-else>
                                        <div class="pick-row">
                                            <button v-for="amt in [100,250,500]" :key="amt" class="btn btn--gold" style="font-size:11px;padding:0 4px;height:36px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(r.id, amt)">
                                                <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                                <template v-else><StarMarkSVG :size="9" /> {{ amt }}</template>
                                            </button>
                                            <div class="amount-field">
                                                <StarMarkSVG class="amount-field__icon" :size="11" />
                                                <input v-model.number="customAmounts[r.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[r.id] && contribute(r.id, customAmounts[r.id])" />
                                            </div>
                                            <button class="btn btn--gold amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[r.id]" @click="contribute(r.id, customAmounts[r.id])">
                                                <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                                <template v-else>→</template>
                                            </button>
                                        </div>
                                    </template>
                                </template>
                                <div v-else class="goal-reached-msg">Goal reached — fulfillment in progress.</div>
                                <ShareStarProductButton :copied="copied === r.id" @share="copyShareLink(r.id)" />
                            </div>
                        </div>
                    </article>
                </div>

                <!-- Bounties -->
                <div v-else-if="activeModal.type === 'bounties'" class="grid grid--3">
                    <article v-for="b in visibleItems" :key="b.id" class="card card--padded">
                        <div class="bounty__head">
                            <div class="avatar" :style="{ background: b.avatarTone }">
                                <img v-if="b.photo" :src="b.photo" :alt="b.name" class="avatar__img" />
                                <template v-else>{{ b.initials }}</template>
                            </div>
                            <div style="flex:1;min-width:0">
                                <div class="bounty__name">{{ b.name }}</div>
                                <div class="muted small">{{ b.lastSeen }}</div>
                            </div>
                            <div class="chip chip--green small">{{ b.momentum }}</div>
                        </div>
                        <div class="card__line">
                            <span class="muted">{{ b.progress.toLocaleString() }} / {{ b.goal.toLocaleString() }}</span>
                            <span style="color:#FB7185;font-weight:700">{{ b.pct }}%</span>
                        </div>
                        <div class="bar"><div class="bar__fill bar__fill--rose" :style="{ width: b.pct + '%' }" /></div>
                        <div class="card__meta" style="margin-bottom:12px"><span class="muted small">{{ b.backers }} members funding</span><span v-if="!b.goalReached && expiryLabel(b.daysLeft)" class="expiry-label" :class="{ 'expiry-label--urgent': isClosingSoon(b.daysLeft) }">{{ expiryLabel(b.daysLeft) }}</span></div>
                        <template v-if="!b.goalReached">
                            <template v-if="b.starCost > 0">
                                <button class="btn btn--rose-soft w-full" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(b.id, b.starCost)">
                                    <span v-if="purchasing" class="btn-spinner" /><StarMarkSVG v-else :size="10" />
                                    {{ purchasing ? 'Processing…' : 'Fund · ' + b.starCost.toLocaleString() + ' stars' }}
                                </button>
                            </template>
                            <template v-else>
                                <div class="pick-row">
                                    <button v-for="amt in [50,200,500]" :key="amt" class="btn btn--rose-soft" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(b.id, amt)">
                                        <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                        <template v-else><StarMarkSVG :size="10" /> {{ amt }}</template>
                                    </button>
                                    <div class="amount-field">
                                        <StarMarkSVG class="amount-field__icon" :size="11" />
                                        <input v-model.number="customAmounts[b.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[b.id] && contribute(b.id, customAmounts[b.id])" />
                                    </div>
                                    <button class="btn btn--rose-soft amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[b.id]" @click="contribute(b.id, customAmounts[b.id])">
                                        <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                        <template v-else>→</template>
                                    </button>
                                </div>
                            </template>
                        </template>
                        <div v-else class="goal-reached-msg">Goal reached — fulfillment in progress.</div>
                        <ShareStarProductButton :copied="copied === b.id" @share="copyShareLink(b.id)" />
                    </article>
                </div>

                <!-- XR -->
                <div v-else-if="activeModal.type === 'xr'" class="grid grid--3">
                    <article v-for="p in visibleItems" :key="p.id" class="card card--padded">
                        <span class="chip chip--xr-label card__leading">{{ p.label }}</span>
                        <div class="bounty__head">
                            <div class="avatar" :style="{ background: p.tone }">
                                <img v-if="p.photo" :src="p.photo" :alt="p.name" class="avatar__img" />
                                <template v-else>{{ p.initials }}</template>
                            </div>
                            <div style="flex:1;min-width:0">
                                <div class="bounty__name">{{ p.name }}</div>
                                <div v-if="p.productName" class="muted small">{{ p.productName }}</div>
                                <div v-if="p.stars > 0" class="muted small"><StarMarkSVG :size="9" style="vertical-align:-1px" /> {{ p.stars.toLocaleString() }} stars contributed</div>
                            </div>
                        </div>
                        <p v-if="p.note" class="muted small" style="margin:0 0 10px">{{ p.note }}</p>
                        <template v-if="p.goal">
                            <div class="card__line">
                                <span class="muted">{{ p.stars.toLocaleString() }} / {{ p.goal.toLocaleString() }}</span>
                                <span style="color:#C4B5FD;font-weight:700">{{ p.pct }}%</span>
                            </div>
                            <div class="bar" style="margin-bottom:10px"><div class="bar__fill bar__fill--violet" :style="{ width: p.pct + '%' }" /></div>
                        </template>
                        <template v-if="!p.goalReached">
                            <template v-if="p.starCost > 0">
                                <button class="btn btn--violet w-full" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(p.id, p.starCost)">
                                    <span v-if="purchasing" class="btn-spinner" /><StarMarkSVG v-else :size="13" />
                                    {{ purchasing ? 'Processing…' : 'Fund · ' + p.starCost.toLocaleString() + ' stars' }}
                                </button>
                            </template>
                            <template v-else>
                                <div class="pick-row">
                                    <button v-for="amt in [100,250,500]" :key="amt" class="btn btn--violet-soft" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(p.id, amt)">
                                        <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                        <template v-else><StarMarkSVG :size="10" /> {{ amt }}</template>
                                    </button>
                                    <div class="amount-field">
                                        <StarMarkSVG class="amount-field__icon" :size="11" />
                                        <input v-model.number="customAmounts[p.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[p.id] && contribute(p.id, customAmounts[p.id])" />
                                    </div>
                                    <button class="btn btn--violet-soft amount-submit" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[p.id]" @click="contribute(p.id, customAmounts[p.id])">
                                        <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                        <template v-else>→</template>
                                    </button>
                                </div>
                            </template>
                        </template>
                        <div v-else class="goal-reached-msg">Goal reached — fulfillment in progress.</div>
                        <ShareStarProductButton :copied="copied === p.id" @share="copyShareLink(p.id)" />
                    </article>
                </div>

                <!-- Series -->
                <div v-else-if="activeModal.type === 'series'" class="grid grid--3">
                    <article v-for="s in visibleItems" :key="s.id" class="card">
                        <div class="card__cover card__cover--sm" :style="{ background: s.cover }">
                            <img v-if="s.logo" :src="s.logo" :alt="s.title" class="series-card__logo" />
                            <div class="card__title-block">
                                <div class="card__eyebrow">{{ s.dormant }}</div>
                                <div v-if="!s.logo" class="card__title">{{ s.title }}</div>
                            </div>
                        </div>
                        <div class="card__body">
                            <div class="card__line">
                                <span class="muted">{{ s.votes.toLocaleString() }} / {{ s.triggerAt.toLocaleString() }} stars</span>
                                <span style="color:#4D94FF;font-weight:700">{{ s.pct }}%</span>
                            </div>
                            <div class="bar"><div class="bar__fill bar__fill--blue" :style="{ width: s.pct + '%' }" /></div>
                            <div v-if="!s.goalReached && expiryLabel(s.daysLeft)" class="card__meta" style="margin:6px 0 2px"><span class="expiry-label" :class="{ 'expiry-label--urgent': isClosingSoon(s.daysLeft) }">{{ expiryLabel(s.daysLeft) }}</span></div>
                            <p v-if="s.note" class="muted small" style="margin:8px 0 10px">{{ s.note }}</p>
                            <div class="card__actions">
                                <template v-if="!s.goalReached">
                                    <template v-if="s.starCost > 0">
                                        <button class="btn btn--blue w-full" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(s.id, s.starCost)">
                                            <span v-if="purchasing" class="btn-spinner" /><StarMarkSVG v-else :size="11" />
                                            {{ purchasing ? 'Processing…' : s.yourVotes > 0 ? 'Fund again · +' + s.starCost.toLocaleString() + ' stars' : 'Fund · ' + s.starCost.toLocaleString() + ' stars' }}
                                        </button>
                                    </template>
                                    <template v-else>
                                        <div class="pick-row">
                                            <button v-for="amt in [50,100,250]" :key="amt" class="btn btn--blue" style="font-size:11px;padding:0 4px;height:36px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(s.id, amt)">
                                                <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                                <template v-else><StarMarkSVG :size="10" /> {{ amt }}</template>
                                            </button>
                                            <div class="amount-field">
                                                <StarMarkSVG class="amount-field__icon" :size="11" />
                                                <input v-model.number="customAmounts[s.id]" type="number" min="1" placeholder="Enter amount…" class="amount-input" @keyup.enter="customAmounts[s.id] && contribute(s.id, customAmounts[s.id])" />
                                            </div>
                                            <button class="btn btn--blue amount-submit" style="font-size:13px;padding:0 12px;height:36px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing || !customAmounts[s.id]" @click="contribute(s.id, customAmounts[s.id])">
                                                <span v-if="purchasing" class="btn-spinner" style="width:10px;height:10px" />
                                                <template v-else>→</template>
                                            </button>
                                        </div>
                                    </template>
                                </template>
                                <div v-else class="goal-reached-msg">Goal reached — fulfillment in progress.</div>
                                <ShareStarProductButton :copied="copied === s.id" @share="copyShareLink(s.id)" />
                            </div>
                        </div>
                    </article>
                </div>

                <!-- Discounts (OF Links + Coupons) -->
                <div v-else-if="activeModal.type === 'discounts' || activeModal.type === 'discounts_oflink' || activeModal.type === 'discounts_coupon' || activeModal.type === 'discounts_fansly'" class="grid grid--4">
                    <DiscountProductCard
                        v-for="o in visibleItems" :key="o.id"
                        :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                        @contribute="contribute(o.product_id, o.price, o.performer_id)"
                        @share="copyShareLink(o.product_id)"
                    />
                </div>

                <!-- Direct Access (Exclusive Clips) -->
                <div v-else-if="activeModal.type === 'direct_access'" class="grid grid--4">
                    <DirectAccessProductCard
                        v-for="o in visibleItems" :key="o.id"
                        :item="o" :purchasing="purchasing" :is-copied="copied === o.product_id"
                        @contribute="contribute(o.product_id, o.price, o.performer_id)"
                        @share="copyShareLink(o.product_id)"
                        @viewed="trackItemViewed"
                        @download="trackItemDownloaded"
                    />
                </div>

                <!-- Flowers -->
                <div v-else-if="activeModal.type === 'flowers'" class="grid grid--4">
                    <article v-for="f in visibleItems" :key="f.id" class="card card--padded">
                        <div class="bounty__head">
                            <div class="avatar avatar--sm" style="width:44px;height:44px;font-size:15px;background:linear-gradient(135deg,#831843,#9D174D)">
                                <img v-if="f.photo" :src="f.photo" :alt="f.name" class="avatar__img" />
                                <template v-else>{{ f.initials }}</template>
                            </div>
                            <div style="flex:1;min-width:0">
                                <div class="bounty__name" style="font-size:15px">{{ f.name }}</div>
                                <div class="muted small">{{ f.product_name }}</div>
                            </div>
                        </div>
                        <p v-if="f.product_description" class="muted small" style="margin:0 0 10px">{{ f.product_description }}</p>
                        <button class="btn btn--pink-soft w-full" style="margin-top:12px" :class="{ 'btn--loading': purchasing }" :disabled="purchasing" @click="contribute(f.product_id, f.price, f.performer_id)">
                            <span v-if="purchasing" class="btn-spinner" />
                            <StarMarkSVG v-else :size="11" />
                            {{ purchasing ? 'Processing…' : 'Send flowers · ' + f.price.toLocaleString() + ' stars' }}
                        </button>
                        <ShareStarProductButton :copied="copied === f.product_id" @share="copyShareLink(f.product_id)" />
                    </article>
                </div>

                <!-- Member's Choice -->
                <div v-else-if="activeModal.type === 'members_choice'" class="mc-list mc-list--modal">
                    <MembersChoiceProductCard
                        v-for="p in visibleItems"
                        :key="p.id"
                        :product="p"
                        :local-votes="localVotes"
                        :vote-burst="voteBurst"
                        :custom-amounts="customAmounts"
                        :voting-option-id="votingOptionId"
                        :is-copied="copied === p.id"
                        :balance="balance"
                        @vote="queueVote"
                        @free-vote="submitFreeVote"
                        @share="copyShareLink"
                        @buy-stars="openBuyStarsModal('star_shop_members_choice')"
                        @option-viewed="trackMcOptionViewed"
                        @cta-clicked="trackMcCtaClick" />
                </div>
            </template>
        </ShopAllModal>

        <template v-if="hasProducts">
        <!-- ────────── FOOTER ────────── -->
        <footer class="footer" :style="{ paddingTop: sectionGap + 'px' }">
            <div class="footer__inner">
                <div class="footer__left">
                    <span><StarIcon name="lock" :size="13" :stroke-width="2" /> Secure checkout</span>
                    <span>·</span><span>Stars expire 1 year after purchase</span>
                </div>
            </div>
        </footer>
        </template>

    </div>
</template>

<!-- ─────────────────────────────────────────────────────────────────── -->
<!-- Helper components: StarMarkSVG (brand star), RowShell, FeaturedProgress, -->
<!-- and MembersChoiceProductCard live in sibling .vue files, imported above.   -->
<!-- ─────────────────────────────────────────────────────────────────── -->

<style scoped>
/* ─── Tokens (scoped) ─── */
.shop {
    --bg: #111120;
    --fg: #fff;
    --mute: #eaeaea;
    --mute-2: #eaeaea;
    --line: rgba(255,255,255,0.13);
    --card: rgba(255,255,255,0.07);
    --gold: #FCD34D;
    --blue: #4D94FF;
    --rose: #FB7185;
    --violet: #C4B5FD;
    --emerald: #34D399;

    position: relative;
    background: var(--bg);
    color: var(--fg);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    min-height: 100vh;
    overflow-x: hidden;
}

/* ── Decorative gutter stars (wide desktop only) ── */
.shop__deco {
    position: absolute;
    top: 0;
    bottom: 0;
    width: calc((100vw - 1280px) / 2);
    max-width: 140px;
    pointer-events: none;
    z-index: 0;
    display: none;
    flex-direction: column;
    align-items: center;
    justify-content: space-around;
    padding: 80px 0 60px;
}
.shop__deco--left  { left: 0; }
.shop__deco--right { right: 0; }
.shop__deco-star {
    flex-shrink: 0;
    width: clamp(24px, 55%, 64px) !important;
    height: auto !important;
    opacity: 0.07;
    animation: star-float 5s ease-in-out infinite;
}
@keyframes star-float {
    0%, 100% { opacity: 0.07; transform: translateY(0)   scale(1);    }
    50%       { opacity: 0.18; transform: translateY(-7px) scale(1.07); }
}
@media (min-width: 1480px) {
    .shop__deco { display: flex; }
}

.shop::before {
    content: '';
    position: fixed; top: 0; left: 0; right: 0; height: 500px;
    background: radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99,60,180,0.18) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
}

/* Type roles */
.display { font-family: 'Manrope', sans-serif; font-weight: 800; font-size: 56px; letter-spacing: -0.03em; line-height: 1.05; margin: 0 0 14px; }
.eyebrow {
    font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.10em;
    color: var(--gold);
    display: inline-flex; align-items: center; gap: 6px;
}
.eyebrow--mute { color: var(--mute); font-size: 10px; }
.eyebrow--center { display: block; text-align: center; margin-bottom: 12px; }
.eyebrow--pill {
    padding: 6px 14px;
    background: rgba(251,191,36,0.12);
    border: 1px solid rgba(251,191,36,0.35);
    border-radius: 9999px; margin-bottom: 18px;
}

.muted { color: var(--mute); }
.small { font-size: 11px; }
.goal-reached-msg { font-size: 12px; color: var(--mute); text-align: center; padding: 8px 0; }
.num { font-family: 'Manrope', sans-serif; font-weight: 800; font-variant-numeric: tabular-nums; }
.num--win { color: var(--emerald); }
.w-full { width: 100%; }
.grad {
    background: linear-gradient(135deg,#FCD34D 0%,#FBBF24 50%,#FCD34D 100%);
    -webkit-background-clip: text; background-clip: text;
    -webkit-text-fill-color: transparent;
}

/* Buttons */
.btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 12px 18px; border-radius: 10px; border: none; cursor: pointer;
    font-size: 14px; font-weight: 700; font-family: inherit;
    transition: transform 120ms ease;
    color: var(--fg);
}
.btn:active { transform: scale(0.985); }
.btn--blue    { background: linear-gradient(135deg,#1a6ee8,#2A318D); box-shadow: 0 8px 24px rgba(0,82,204,0.35); }
.btn--gold    { background: linear-gradient(135deg,#FCD34D,#F59E0B); color: #0A0A0F; box-shadow: 0 8px 24px rgba(251,191,36,0.35); font-weight: 800; }
.btn--violet  { background: linear-gradient(135deg,#9B6EFA,#6D28D9); box-shadow: 0 8px 24px rgba(139,92,246,0.35); }
.btn--emerald { background: linear-gradient(135deg,#10B981,#047857); box-shadow: 0 8px 24px rgba(16,185,129,0.35); }
.btn--ghost   { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.22); }
.btn--ghost-out { background: transparent; border: 1px solid rgba(255,255,255,0.14); }
.btn--gold-outline { background: transparent; border: 1px solid rgba(251,191,36,0.45); color: var(--gold); font-size: 12px; padding: 8px 12px; }
.btn--blue-outline { background: transparent; border: 1px solid rgba(0,82,204,0.45); color: var(--blue); font-size: 12px; padding: 8px 12px; }
.btn--blue-active  { background: rgba(0,82,204,0.18); }
.btn--rose-soft { background: rgba(244,63,94,0.08); border: 1px solid rgba(244,63,94,0.30); color: var(--rose); font-size: 12px; padding: 0 4px; height: 36px; gap: 3px; }

/* ─── (Header handled by HeaderV2 component) ─── */

/* ─── Hero ─── */
.hero { position: relative; padding: 72px 32px 56px; max-width: 1280px; margin: 0 auto; }
.hero__glow { display: none; }
.hero__star { position: absolute; opacity: 0.20; }
.hero__star--a { top: 80px;    right: 240px; animation: drift-a 9s ease-in-out infinite; }
.hero__star--b { bottom: 60px; right: 380px; animation: drift-b 11s ease-in-out infinite; animation-delay: 1.5s; }
.hero__star--c { top: 40px;    right: 560px; animation: drift-c 7s ease-in-out infinite;  animation-delay: 0.8s; opacity: 0.15; }
.hero__star--d { top: 160px;   right: 100px; animation: drift-a 13s ease-in-out infinite; animation-delay: 2.2s; opacity: 0.12; }
.hero__star--e { bottom: 100px; right: 200px; animation: drift-b 6s ease-in-out infinite; animation-delay: 0.3s; opacity: 0.25; }
.hero__star--f { top: 120px;   right: 460px; animation: drift-c 15s ease-in-out infinite; animation-delay: 3.5s; opacity: 0.10; }
.hero__star--g { bottom: 40px; right: 480px; animation: drift-a 8s ease-in-out infinite;  animation-delay: 1.0s; opacity: 0.30; }
.hero__grid { position: relative; z-index: 1; display: grid; grid-template-columns: 1.4fr 1fr; gap: 56px; align-items: center; }
.hero__h1 { font-family: 'Manrope', sans-serif; font-size: 62px; font-weight: 900; letter-spacing: -0.035em; line-height: 1.0; margin: 0 0 22px; }
.hero__sub { font-size: 17px; line-height: 1.55; color: var(--mute); margin: 0; max-width: 540px; }

.balance-card {
    background: #232323;
    border: 1px solid rgba(255,255,255,0.10);
    border-top: 2px solid rgba(255,255,255,0.15);
    border-radius: 20px; padding: 24px 28px;
    box-shadow: none;
}
.balance-card__row { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
.balance-card__star-wrap {
    display: inline-flex;
    animation: star-float 3s ease-in-out infinite;
    filter: drop-shadow(0 0 8px rgba(251,191,36,0.6));
}
.balance-card__num { font-family: 'Manrope', sans-serif; font-size: 60px; font-weight: 900; letter-spacing: -0.035em; line-height: 1; font-variant-numeric: tabular-nums; }
.balance-card__lbl { font-size: 15px; color: var(--mute); font-weight: 500; }
.balance-card__sub { font-size: 12px; color: var(--mute); margin-bottom: 16px; font-variant-numeric: tabular-nums; }
.balance-card__actions { display: flex; gap: 8px; }
.balance-card__actions .btn { flex: 1; }
.balance-card__actions .btn--ghost { flex: 0 0 auto; padding: 12px 14px; }

.balance-card--guest { display: flex; flex-direction: column; gap: 10px; }
.balance-card__guest-icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 56px; height: 56px; border-radius: 50%;
    background: rgba(251,191,36,0.10); border: 1px solid rgba(251,191,36,0.25);
    filter: drop-shadow(0 0 10px rgba(251,191,36,0.35));
}
.balance-card__guest-h { font-family: 'Manrope', sans-serif; font-size: 20px; font-weight: 800; letter-spacing: -0.01em; line-height: 1.2; margin: 0; }
.balance-card__guest-sub { font-size: 13px; color: var(--mute); margin: 0; line-height: 1.5; }
.balance-card--guest .balance-card__actions { margin-top: 4px; }
.balance-card--guest .balance-card__actions .btn--ghost { flex: 1; }

/* ─── Featured slot ─── */
.featured { max-width: 1280px; margin: 0 auto; padding: 0 32px; }
.featured__inner {
    position: relative; min-height: 440px; border-radius: 24px; overflow: hidden;
    background: linear-gradient(135deg,#4a0a1a 0%,#8c1f35 60%,#4a0a1a 100%);
    border: 1px solid rgba(251,113,133,0.20);
}
.featured--remaster .featured__inner { background: linear-gradient(135deg,#362208 0%,#6b521a 60%,#362208 100%); border-color: rgba(251,191,36,0.20); }
.featured--xr       .featured__inner { background: linear-gradient(135deg,#1e0f42 0%,#4a228a 60%,#1e0f42 100%); border-color: rgba(196,181,253,0.20); }
.featured--series   .featured__inner { background: linear-gradient(135deg,#0d1e3d 0%,#1a3a7a 60%,#0d1e3d 100%); border-color: rgba(77,148,255,0.20); }
.featured--oflink   .featured__inner { background: linear-gradient(135deg,#032028 0%,#065e72 60%,#032028 100%); border-color: rgba(103,232,249,0.20); }
.featured--flowers  .featured__inner { background: linear-gradient(135deg,#2d0a1e 0%,#6b1a3f 60%,#2d0a1e 100%); border-color: rgba(249,168,212,0.20); }
.featured--members_choice .featured__inner { background: linear-gradient(135deg,#062018 0%,#0f5132 60%,#062018 100%); border-color: rgba(52,211,153,0.20); }

.featured__bg-img {
    position: absolute; right: 0; top: 0;
    width: 50%; height: 110%;
    object-fit: cover; object-position: top center;
    z-index: 0;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 20%, black 45%);
    mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,0.5) 20%, black 45%);
}
.featured__overlay { position: absolute; inset: 0; z-index: 1; background: linear-gradient(95deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.60) 40%, rgba(0,0,0,0.10) 65%, transparent 100%); }
.featured__refresh {
    position: absolute; top: 16px; right: 16px; z-index: 3;
    display: flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
    color: rgba(255,255,255,0.5); cursor: pointer; transition: background 0.15s, color 0.15s, transform 0.3s;
}
.featured__refresh:hover { background: rgba(255,255,255,0.15); color: rgba(255,255,255,0.9); }
.featured__refresh:active { transform: rotate(180deg); }
.featured__refresh--over-image {
    background: rgba(255,255,255,0.25); border-color: rgba(255,255,255,0.75);
    color: white; mix-blend-mode: difference; opacity: 0.65; transition: opacity 0.15s, transform 0.3s;
}
.featured__refresh--over-image:hover { background: rgba(255,255,255,0.25); color: white; opacity: 1; }
.featured__copy { position: relative; z-index: 2; padding: 52px 56px; display: flex; flex-direction: column; gap: 18px; max-width: 720px; }
/* Members Choice has no single hero image to show alongside the copy (the
   options themselves are the visual) — give it the extra width the image
   column would otherwise occupy so 3 option thumbnails fit in a row. */
.featured--members_choice .featured__copy { max-width: 980px; }

/* Horizontal swipe/scroll row — same pattern as MembersChoiceProductCard's
   options row: fixed-width tiles so a 4th/5th option scrolls in rather than
   shrinking the others, with an edge fade hinting there's more to swipe to. */
.mc-featured-options-wrap { position: relative; }
/* Sibling of the scrolling row (not inside it) so overflow-x: auto on
   .mc-featured-options can't clip the burst's radiating particles. Sized to
   the wrap and non-interactive — CelebrationBurst centers itself (left:50%)
   and anchors 64px from this box's top, landing over the video row. */
.mc-featured-burst-anchor { position: absolute; inset: 0; pointer-events: none; z-index: 3; }
.mc-featured-options-frame {
    /* Height comes from the inline mcFeaturedFrameHeight style (measured off
       the actual media element) — NOT 100%, which spanned the whole tile
       column (media + title/description/tally/Vote button) and bled the
       scroll-hint fade down over the controls instead of just the videos. */
    position: absolute; top: 0; left: 0; right: 0;
    z-index: 2; pointer-events: none; overflow: hidden;
}
.mc-featured-options-fade {
    position: absolute; top: 0; bottom: 0; width: 44px;
    opacity: 1; transition: opacity 0.2s ease;
}
.mc-featured-options-fade--left { left: 0; border-radius: 12px 0 0 12px; background: linear-gradient(to right, rgba(6,32,24,0.9), transparent); }
.mc-featured-options-fade--right { right: 0; border-radius: 0 12px 12px 0; background: linear-gradient(to left, rgba(6,32,24,0.9), transparent); }
.mc-featured-options-frame--at-start .mc-featured-options-fade--left { opacity: 0; }
.mc-featured-options-frame--at-end .mc-featured-options-fade--right { opacity: 0; }

/* Sizing/format below mirrors MembersChoiceProductCard.vue's .mc-option
   tiles exactly (same 200px width, 24px gap, tally/pct/bar layout, 36px
   full-width vote button) so the featured slot and the row read as the
   same design, not a scaled-down variant. */
.mc-featured-options { display: flex; gap: 24px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; scrollbar-width: none; overscroll-behavior-x: contain; }
.mc-featured-options::-webkit-scrollbar { display: none; }
.mc-featured-options--scrollable { cursor: grab; }
.mc-featured-option {
    flex: 0 0 200px; max-width: 200px; min-width: 0; text-align: left;
    display: flex; flex-direction: column;
    scroll-snap-align: start;
}
.mc-featured-option__media {
    position: relative; aspect-ratio: 4 / 5;
    border-radius: 12px; overflow: hidden; box-sizing: border-box;
    border: 2px solid rgba(255,255,255,0.15);
    background: linear-gradient(135deg,#1f2937,#111827);
    /* A decoding <video> is promoted to its own GPU compositor layer, which
       can bleed decoded frame pixels past this box's rounded overflow:hidden
       clip. contain: paint plus border-radius on the video itself (below)
       are a hard guarantee nothing painted here escapes the box either way. */
    contain: paint;
}
.mc-featured-option__media img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.mc-featured-option__media video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; cursor: pointer; border-radius: inherit; }
.mc-featured-option--leading .mc-featured-option__media { border-color: rgba(52,211,153,0.85); }
.mc-featured-option--winner .mc-featured-option__media { border-color: rgba(252,211,77,0.9); box-shadow: 0 0 24px rgba(252,211,77,0.12); }
.mc-featured-option__play {
    position: absolute; inset: 0; z-index: 1;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none; color: #fff;
}
.mc-featured-option__play svg {
    width: 44px; height: 44px; padding: 12px; border-radius: 50%;
    background: rgba(0,0,0,0.45);
}
.mc-featured-option .mc-ribbon {
    position: absolute; top: 8px; left: 8px;
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    padding: 4px 10px; border-radius: 9999px;
    background: rgba(252,211,77,0.95); color: #1a1502;
}
.mc-featured-option .mc-leading {
    position: absolute; top: 8px; left: 8px;
    font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;
    padding: 3px 9px; border-radius: 9999px;
    background: rgba(4,20,14,0.82); backdrop-filter: blur(4px);
    border: 1px solid rgba(52,211,153,0.55); color: #34D399;
}

/* Solid backdrop of its own — title/description/tally/Vote button have no
   background otherwise, so they were relying on .featured__overlay behind
   them for contrast. That overlay fades to transparent well before this
   tile's position once the column is wide enough to fit 3-4 tiles, letting
   the hero photo show straight through the text. The video box already has
   its own opaque fill, so it stays clean regardless — this keeps the bleed
   confined to just that box instead of the option's text/button too. */
.mc-featured-option__body { margin-top: 8px; padding: 10px; border-radius: 10px; background: #04100c; display: flex; flex-direction: column; flex: 1; min-width: 0; }
.mc-featured-option__text { min-height: 42px; }
.mc-featured-option__title { font-size: 13px; font-weight: 700; color: #fff; }
.mc-featured-option .mc-muted { color: rgba(234,234,234,0.75); font-size: 12px; margin: 0; }
.mc-featured-option__desc { margin-top: 4px; }
.mc-featured-option__tally { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 8px 0 6px; }
.mc-featured-option__tally-left { display: flex; align-items: center; gap: 5px; min-width: 0; color: rgba(255,255,255,0.7); }
.mc-featured-option__count { font-family: 'Manrope', sans-serif; font-weight: 800; font-variant-numeric: tabular-nums; font-size: 14px; color: #fff; }
.mc-featured-option__pct { font-family: 'Manrope', sans-serif; font-weight: 800; font-variant-numeric: tabular-nums; font-size: 14px; color: #fff; flex-shrink: 0; }
.mc-featured-bar { height: 5px; background: rgba(255,255,255,0.10); border-radius: 9999px; overflow: hidden; margin-bottom: 14px; }
.mc-featured-bar__fill { height: 100%; background: #34D399; border-radius: 9999px; transition: width 0.3s ease; }

.mc-featured-option__vote { position: relative; min-height: 36px; }
.mc-featured-vote-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    height: 36px; padding: 0 12px; border-radius: 9px;
    background: rgba(52,211,153,0.14); border: 1px solid rgba(52,211,153,0.45); color: #34D399;
    font-size: 12px; font-weight: 700; cursor: pointer; transition: background 0.15s, transform 0.08s;
}
.mc-featured-vote-btn:hover:not(:disabled) { background: rgba(52,211,153,0.24); }
.mc-featured-vote-btn:active:not(:disabled) { transform: scale(0.96); }
.mc-featured-vote-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.mc-featured-vote-btn--full { width: 100%; }
.mc-featured-vote-btn--submit { padding: 0 12px; flex-shrink: 0; }
.mc-featured-vote-expanded {
    display: flex; flex-direction: column; gap: 8px;
    padding: 10px; border-radius: 10px;
    border: 1px solid rgba(52,211,153,0.35); background: rgba(52,211,153,0.06);
}
.mc-featured-pick-row { display: flex; gap: 6px; }
.mc-featured-pick-btn {
    flex: 1; height: 36px; padding: 0 4px; border-radius: 9px;
    display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    background: rgba(52,211,153,0.14); border: 1px solid rgba(52,211,153,0.45); color: #34D399;
    font-size: 11px; font-weight: 700; cursor: pointer; transition: background 0.15s;
}
.mc-featured-pick-btn:hover:not(:disabled) { background: rgba(52,211,153,0.24); }
.mc-featured-pick-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.mc-featured-amount-row { display: flex; gap: 6px; }
.mc-featured-amount-input {
    flex: 1; min-width: 0; height: 36px; padding: 0 8px;
    background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.15); border-radius: 9px;
    color: #fff; font-size: 12px;
}
.mc-featured-amount-input::placeholder { color: rgba(234,234,234,0.5); }
.mc-featured-vote-cancel {
    background: none; border: none; color: rgba(234,234,234,0.6);
    font-size: 12px; text-align: center; cursor: pointer; padding: 2px 0 0;
}
.mc-featured-vote-cancel:hover { color: #fff; text-decoration: underline; }
.featured__h2 { font-family: 'Manrope', sans-serif; font-size: 56px; font-weight: 900; letter-spacing: -0.03em; line-height: 1.0; margin: 0; }
.featured__sub { font-size: 17px; color: rgba(255,255,255,0.78); margin: 0; line-height: 1.5; max-width: 600px; }
.featured__actions { display: flex; gap: 12px; flex-wrap: wrap; }
.featured__actions .btn--blue, .featured__actions .btn--gold, .featured__actions .btn--violet, .featured__actions .btn--emerald { padding: 14px 24px; font-size: 15px; }
.featured__actions .btn--ghost { padding: 14px 22px; background: rgba(255,255,255,0.06); }


.status {
    display: inline-flex; align-items: center; gap: 8px; padding: 5px 12px;
    border-radius: 9999px; font-size: 11px; font-weight: 800; letter-spacing: 0.10em;
    text-transform: uppercase; width: fit-content;
}
.status--rose    { background: rgba(244,63,94,0.20); border: 1px solid rgba(244,63,94,0.40); color: var(--rose); }
.status--gold    { background: rgba(251,191,36,0.20); border: 1px solid rgba(251,191,36,0.40); color: var(--gold); }
.status--violet  { background: rgba(139,92,246,0.20); border: 1px solid rgba(196,181,253,0.40); color: var(--violet); }
.status--blue    { background: rgba(0,82,204,0.20); border: 1px solid rgba(0,82,204,0.40); color: var(--blue); }
.status--emerald { background: rgba(16,185,129,0.20); border: 1px solid rgba(52,211,153,0.40); color: var(--emerald); }
.status--lime    { background: rgba(163,230,53,0.20); border: 1px solid rgba(163,230,53,0.40); color: #A3E635; }

.kpi-row { display: flex; align-items: center; gap: 18px; }
.kpi__l { font-size: 11px; color: var(--mute); text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; margin-bottom: 4px; }
.kpi__v { font-family: 'Manrope', sans-serif; font-size: 24px; font-weight: 800; font-variant-numeric: tabular-nums; }
.kpi__sep { width: 1px; height: 36px; background: var(--line); }

/* Featured progress bar */
.fp { margin: 4px 0 8px; }
.fp__row { display: flex; align-items: baseline; gap: 12px; margin-bottom: 10px; }
.fp__n { font-family: 'Manrope', sans-serif; font-size: 36px; font-weight: 900; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
.fp__g { color: var(--mute); font-size: 16px; }
.fp__bar { height: 10px; background: rgba(255,255,255,0.10); border-radius: 9999px; overflow: hidden; max-width: 540px; }
.fp__fill { height: 100%; border-radius: 9999px; }

/* ─── Catalog header ─── */
.catalog-head { max-width: 1280px; margin: 0 auto; padding: 88px 32px 56px; text-align: center; }
.catalog-head__sub { font-size: 16px; color: var(--mute); max-width: 560px; margin: 0 auto; }

/* ─── Rows ─── */
.rows { max-width: 1280px; margin: 0 auto; padding: 0 32px; display: flex; flex-direction: column; }
.row {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 24px;
    padding: 32px;
}
.row__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 32px; margin-bottom: 24px; }
.row__copy { max-width: 620px; }
.row__meta { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.row__idx { font-family: 'Manrope', sans-serif; font-size: 13px; font-weight: 800; letter-spacing: 0.05em; }
.row__hairline { width: 24px; height: 1px; background: rgba(255,255,255,0.10); }
.row__h3 { font-family: 'Manrope', sans-serif; font-size: 36px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.05; margin: 0 0 12px; }
.row__sub { font-size: 15px; color: var(--mute); line-height: 1.55; margin: 0; max-width: 560px; }
.row__foot { margin-top: 20px; display: flex; align-items: center; justify-content: space-between; }
.row__cta {
    padding: 10px 16px; border-radius: 10px; text-decoration: none;
    font-size: 13px; font-weight: 700;
    background: transparent; border: 1px solid;
}
.tag-new {
    font-size: 9px; font-weight: 900; letter-spacing: 0.12em;
    padding: 2px 6px; border-radius: 4px;
    background: rgba(251,191,36,0.15); color: var(--gold);
    border: 1px solid rgba(251,191,36,0.30);
}

/* Mechanic + Signal panel */

/* Grids */
.grid { display: grid; gap: 14px; }
.grid--3 { grid-template-columns: repeat(3, 1fr); }
.grid--4 { grid-template-columns: repeat(4, 1fr); }
.grid--5 { grid-template-columns: repeat(5, 1fr); gap: 10px; }

/* Cards */
.card {
    background: linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%);
    border: 1px solid var(--line);
    border-radius: 14px; overflow: hidden; position: relative;
    box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset;
}
.card--padded { padding: 16px; display: flex; flex-direction: column; }
.card--padded > .card__line { margin-top: auto; }
.card--leading { border-color: rgba(0,82,204,0.40); }
@keyframes cardFlash {
    0%   { outline-color: transparent; }
    20%  { outline-color: #FCD34D; }
    80%  { outline-color: #FCD34D; }
    100% { outline-color: transparent; }
}
.card--highlight {
    outline: 2px solid transparent;
    outline-offset: 3px;
    animation: cardFlash 2.5s ease forwards;
}
.card__cover { height: 200px; position: relative; padding: 12px; }
.card__cover--sm { height: 100px; }
.card__cover::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.85), transparent 60%);
    pointer-events: none; z-index: 1;
}
.card__cover-img {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: cover; object-position: center top;
}
/* right offset clears the corner share button */
.card__year { position: absolute; top: 10px; right: 42px; z-index: 2; }
.card__leading { position: absolute; top: 10px; right: 42px; z-index: 2; }
.card__title-block { position: absolute; bottom: 10px; left: 12px; right: 12px; z-index: 2; }
.card__eyebrow { font-size: 10px; color: rgba(255,255,255,0.55); text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; }
.card__title { font-family: 'Manrope', sans-serif; font-size: 16px; font-weight: 800; letter-spacing: -0.01em; }
.card__body { padding: 14px; display: flex; flex-direction: column; }
.card__actions { margin-top: auto; }
.card__line { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 5px; font-variant-numeric: tabular-nums; }
.card__meta { display: flex; align-items: center; justify-content: space-between; font-size: 11px; color: var(--mute); margin-bottom: 10px; }
.expiry-label { font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.45); }
.expiry-label--urgent { color: #FB7185; }

/* Chips */
.chip {
    font-size: 9px; font-weight: 900; letter-spacing: 0.10em; text-transform: uppercase;
    padding: 3px 8px; border-radius: 9999px;
    display: inline-flex; align-items: center; gap: 4px;
}
.chip--rose  { background: rgba(244,63,94,0.20); color: var(--rose); border: 1px solid rgba(251,113,133,0.40); position: absolute; top: 10px; left: 10px; z-index: 2; }
.chip--mute  { background: rgba(0,0,0,0.55); color: rgba(255,255,255,0.85); border: 1px solid rgba(255,255,255,0.15); }
.chip--blue  { background: rgba(0,82,204,0.25); color: var(--blue); border: 1px solid rgba(0,82,204,0.45); }
.chip--green { background: rgba(52,211,153,0.10); color: var(--emerald); border: 1px solid rgba(52,211,153,0.30); white-space: nowrap; }

/* Series card logo */
.series-card__logo {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: contain;
    padding: 14px;
}

/* Progress bar */
.bar { height: 6px; background: rgba(255,255,255,0.10); border-radius: 9999px; overflow: hidden; margin-bottom: 12px; }
.bar__fill { height: 100%; }
.bar__fill--gold { background: linear-gradient(90deg,#FCD34D,#FBBF24); }
.bar__fill--rose { background: linear-gradient(90deg,#F43F5E,#FB7185); margin-bottom: 0; }
.bar__fill--blue { background: linear-gradient(90deg,#0052CC,#4D94FF); }
.bar__fill--violet { background: linear-gradient(90deg,#7C3AED,#C4B5FD); }

/* Bounty */
/* right padding clears the corner share button */
.bounty__head { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; padding-right: 26px; }
.bounty__name { font-family: 'Manrope', sans-serif; font-size: 18px; font-weight: 800; letter-spacing: -0.01em; margin-bottom: 3px; }

/* Avatar */
.avatar {
    width: 64px; height: 64px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 900; color: #fff;
    flex-shrink: 0; overflow: hidden; position: relative;
}
.avatar__img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: top center; }
.avatar--sm { width: 36px; height: 36px; font-size: 13px; }
.avatar--md { width: 60px; height: 60px; font-size: 20px; margin: 4px auto 10px; }

.btn--violet-soft { background: rgba(139,92,246,0.10); border: 1px solid rgba(196,181,253,0.30); color: var(--violet); font-size: 12px; padding: 0 4px; height: 36px; gap: 3px; }


/* Member's Choice — card itself lives in MembersChoiceProductCard.vue; this carousel
   pages between products (one visible at a time), it does not touch a product's options.
   Arrows live in .mc-carousel__stage (card only) rather than .mc-carousel (card + dots),
   so their top:50% centers on the card's own height instead of drifting toward the dots. */
.mc-carousel__stage { position: relative; }
.mc-carousel__arrow {
    position: absolute; top: 50%; transform: translateY(-50%); z-index: 2;
    width: 48px; height: 48px; border-radius: 9999px;
    background: rgba(10,10,14,0.85); border: 1px solid rgba(255,255,255,0.30); color: #fff;
    box-shadow: 0 4px 16px rgba(0,0,0,0.35);
    cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.08s;
}
.mc-carousel__arrow:hover:not(:disabled) { background: #34D399; border-color: #34D399; color: #0a0a0e; }
.mc-carousel__arrow:active:not(:disabled) { transform: translateY(-50%) scale(0.96); }
.mc-carousel__arrow:disabled { opacity: 0.35; cursor: not-allowed; }
.mc-carousel__arrow--left { left: 4px; }
.mc-carousel__arrow--right { right: 4px; }

/* "See all" modal list — each MembersChoiceProductCard already has its own
   border, so a gap (not just margin) keeps that border from touching the
   next card's border with zero breathing room between them. */
.mc-list--modal { display: flex; flex-direction: column; gap: 24px; }

/* Native scroll-snap track — real touch/trackpad swipe, same at every
   breakpoint. Each card takes the full track width (one product visible at
   a time, no peek of the next); scroll-padding-inline keeps the first/last
   card snapping flush against the track's own edge instead of stopping
   short. Scrollbar hidden since the arrows/dots are the visible nav. */
.mc-carousel__track {
    display: flex; gap: 16px; overflow-x: auto;
    /* proximity, not mandatory — mandatory fights a slow/deliberate swipe on
       real touch devices, yanking it back to the nearest card if it doesn't
       carry enough velocity; proximity still snaps near a card but lets a
       soft drag settle wherever the finger left it. */
    scroll-snap-type: x proximity; scroll-padding-inline: 0;
    -webkit-overflow-scrolling: touch; scrollbar-width: none;
}
.mc-carousel__track::-webkit-scrollbar { display: none; }
.mc-carousel__card {
    flex: 0 0 100%; scroll-snap-align: start;
    /* Flex items default to min-width: auto, so without this a product with
       enough options to overflow (e.g. 5 options wanting ~1096px) pulls the
       WHOLE card wider than its own basis — measured one stretch to 102% of
       the track. That's why more-option products rendered visibly SMALLER
       photos: same tile formula, calc((100% - 48px) / 3), just dividing up a
       now-larger .mc-options-row. min-width: 0 stops that growth so
       .mc-options-row's own overflow-x: auto scrolls instead of expanding
       its ancestors, keeping every card (and every tile) the same size
       regardless of option count. */
    min-width: 0;
}

.mc-carousel__dots { display: flex; justify-content: center; gap: 8px; margin-top: 16px; }
.mc-carousel__dot {
    width: 8px; height: 8px; border-radius: 9999px; padding: 0; cursor: pointer;
    background: rgba(255,255,255,0.20); border: none;
    transition: background 0.15s, transform 0.15s;
}
.mc-carousel__dot--active { background: #34D399; transform: scale(1.2); }

/* On mobile the card's own compact header (MembersChoiceProductCard.vue) stacks
   above the options instead of sitting beside them, making the card much taller —
   these arrows' top:50% then lands mid-way down inside the options video instead
   of anywhere sensible. Hide them there; the track itself is still swipeable
   (native scroll-snap, same track as desktop) and the dots below page products
   too — so size the dots up to read as real navigation rather than a subtle
   progress hint, not the only way to move between products.
   NOTE: this block must stay AFTER the unconditional .mc-carousel__dot* rules
   above — same specificity, so whichever is declared later wins the cascade at
   every viewport the media query matches; declared first, it would just get
   clobbered right back by those rules (this exact bug shipped once already). */
@media (max-width: 640px) {
    .mc-carousel__arrow { display: none; }

    /* Grouped into a pill so it reads as a distinct nav control (not a subtle
       progress hint), with the actual tappable button sized to a proper ~44px
       touch target — the visible dot itself stays small via a ::before, so a
       bigger hit area doesn't just look like bigger dots crowding together. */
    .mc-carousel__dots {
        gap: 2px; margin: 20px auto 0; width: fit-content;
        background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10);
        border-radius: 9999px; padding: 0 6px;
    }
    .mc-carousel__dot {
        width: 44px; height: 44px; position: relative;
        background: none; transform: none;
    }
    .mc-carousel__dot::before {
        content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
        width: 10px; height: 10px; border-radius: 9999px;
        background: rgba(255,255,255,0.25);
        transition: background 0.15s, width 0.15s;
    }
    .mc-carousel__dot--active::before { background: #34D399; width: 26px; }
}

/* Vault Clips — several product cards peek at once (unlike Member's Choice's
   one-at-a-time pager), so arrows sit beside the track rather than overlaid
   on top of a single card, and there's no dot pager to track a "current" card. */
.vault-carousel { display: flex; align-items: center; gap: 12px; }
.vault-carousel__track {
    flex: 1; display: flex; gap: 14px; overflow-x: auto;
    scroll-snap-type: x proximity; scroll-padding-inline: 0;
    -webkit-overflow-scrolling: touch; scrollbar-width: none;
}
.vault-carousel__track::-webkit-scrollbar { display: none; }
.vault-carousel__card { flex: 0 0 clamp(210px, 19vw, 245px); scroll-snap-align: start; min-width: 0; }
.vault-carousel__arrow {
    flex: 0 0 auto; width: 40px; height: 40px; border-radius: 9999px;
    background: rgba(10,10,14,0.85); border: 1px solid rgba(255,255,255,0.30); color: #fff;
    box-shadow: 0 4px 16px rgba(0,0,0,0.35);
    cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    transition: background 0.15s, border-color 0.15s, color 0.15s, transform 0.08s;
}
.vault-carousel__arrow:hover:not(:disabled) { background: #FCD34D; border-color: #FCD34D; color: #0a0a0e; }
.vault-carousel__arrow:active:not(:disabled) { transform: scale(0.96); }
.vault-carousel__arrow:disabled { opacity: 0.35; cursor: not-allowed; }

/* ─── Staircase ─── */
.staircase { position: relative; max-width: 1280px; margin: 0 auto; padding-left: 32px; padding-right: 32px; overflow: hidden; }
.staircase__glow { display: none; }
.staircase__head { position: relative; text-align: center; margin-bottom: 48px; }
.staircase__grid {
    position: relative; display: flex; gap: 14px; align-items: flex-end;
    overflow-x: auto; padding-bottom: 8px;
    scroll-snap-type: x proximity; -webkit-overflow-scrolling: touch;
}
.staircase__grid::-webkit-scrollbar { height: 3px; }
.staircase__grid::-webkit-scrollbar-track { background: transparent; }
.staircase__grid::-webkit-scrollbar-thumb { background: rgba(251,191,36,0.25); border-radius: 2px; }
.pack {
    position: relative; padding: 20px; border-radius: 20px;
    background: rgba(255,255,255,0.03); border: 1px solid var(--line);
    display: flex; flex-direction: column; justify-content: space-between;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    flex: 0 0 175px; scroll-snap-align: start;
}
.pack:hover { transform: translateY(-3px); }
.pack--popular {
    background: rgba(0,82,204,0.12);
    border-color: rgba(77,148,255,0.45);
    box-shadow: 0 0 30px rgba(0,82,204,0.15);
}
.pack--best {
    background: rgba(251,191,36,0.10);
    border-color: rgba(251,191,36,0.50);
    box-shadow: 0 0 40px rgba(251,191,36,0.18);
}
.pack__pill {
    position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
    font-size: 10px; font-weight: 900; letter-spacing: 0.10em; text-transform: uppercase;
    padding: 4px 10px; border-radius: 9999px; white-space: nowrap;
}
.pack__pill--blue { background: linear-gradient(90deg,#0052CC,#4D94FF); color: #fff; box-shadow: 0 2px 10px rgba(0,82,204,0.40); }
.pack__pill--gold { background: linear-gradient(90deg,#FBBF24,#FCD34D); color: #0A0A0F; box-shadow: 0 2px 10px rgba(251,191,36,0.40); }
.pack__count { display: flex; align-items: flex-end; gap: 6px; margin: 8px 0 4px; }
.pack__num { font-family: 'Manrope', sans-serif; font-weight: 900; letter-spacing: -0.02em; line-height: 1; font-variant-numeric: tabular-nums; }
.pack__num--gold { color: var(--gold); }
.pack__price { font-family: 'Manrope', sans-serif; font-weight: 800; letter-spacing: -0.01em; font-variant-numeric: tabular-nums; }
.pack__price--muted { color: rgba(255,255,255,0.70); }

.btn--tos-locked { opacity: 0.35; cursor: not-allowed; }

.staircase__tos {
    margin-top: 32px; display: flex; justify-content: center;
}
.staircase__tos-label {
    display: flex; align-items: flex-start; gap: 10px; cursor: pointer;
    font-size: 13px; color: var(--mute); line-height: 1.5; max-width: 480px;
}
.staircase__tos-check {
    margin-top: 2px; width: 16px; height: 16px; flex-shrink: 0;
    appearance: none; -webkit-appearance: none;
    border: 1.5px solid rgba(255,255,255,0.35); border-radius: 3px;
    background: transparent; cursor: pointer; position: relative;
    transition: border-color 0.15s, background 0.15s;
}
.staircase__tos-check:checked {
    background: #FCD34D; border-color: #FCD34D;
}
.staircase__tos-check:checked::after {
    content: '';
    position: absolute; left: 4px; top: 1px;
    width: 5px; height: 9px;
    border: 2px solid #111; border-top: none; border-left: none;
    transform: rotate(45deg);
}
.staircase__tos-link { color: var(--gold); text-underline-offset: 2px; }
.staircase__tos-link:hover { text-decoration: underline; }
.staircase__tos--shake {
    animation: tos-shake 0.55s ease;
}
.staircase__tos--shake .staircase__tos-check {
    border-color: #FCD34D;
    box-shadow: 0 0 0 3px rgba(252,211,77,0.35);
}
@keyframes tos-shake {
    0%, 100% { transform: translateX(0); }
    15%       { transform: translateX(-6px); }
    30%       { transform: translateX(5px); }
    45%       { transform: translateX(-4px); }
    60%       { transform: translateX(3px); }
    75%       { transform: translateX(-2px); }
}

/* ─── OF Discount Links + Flowers (new mechanics) ─── */
.btn--cyan { background: linear-gradient(135deg,#22D3EE,#0891B2); color: #04222a; font-weight: 800; box-shadow: 0 8px 20px rgba(34,211,238,0.22); }
.btn--amber { background: linear-gradient(135deg,#FB923C,#EA580C); color: #3a1a02; font-weight: 800; box-shadow: 0 8px 20px rgba(251,146,60,0.22); }
.btn--lime { background: linear-gradient(135deg,#A3E635,#65A30D); color: #1a2e05; font-weight: 800; box-shadow: 0 8px 20px rgba(163,230,53,0.22); }
.btn--pink-soft { background: rgba(236,72,153,0.10); border: 1px solid rgba(249,168,212,0.40); color: var(--pink); font-size: 12px; padding: 8px 12px; }
.bar__fill--cyan { background: linear-gradient(90deg,#0891B2,#67E8F9); }
.chip--cyan { background: rgba(34,211,238,0.15); color: #67E8F9; border: 1px solid rgba(103,232,249,0.40); }
.chip--pink { background: rgba(236,72,153,0.18); color: var(--pink); border: 1px solid rgba(249,168,212,0.40); }
.chip--xr-label { background: rgba(196,181,253,0.15); color: #C4B5FD; border: 1px solid rgba(196,181,253,0.35); }

.of-offer { display: flex; align-items: center; gap: 10px; position: relative; margin-bottom: 14px; }
.of-offer__num { font-family: 'Manrope', sans-serif; font-size: 40px; font-weight: 900; letter-spacing: -0.03em; line-height: 1; color: var(--offer-accent, #67E8F9); }
.of-offer__lbl { font-size: 11px; line-height: 1.25; color: var(--mute); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; }
.of-offer__chip { position: absolute; top: 0; right: 0; }


/* ─── Footer ─── */
.footer { max-width: 1280px; margin: 0 auto; padding: 88px 32px 80px; }
.footer__inner { border-top: 1px solid rgba(255,255,255,0.06); padding-top: 36px; display: flex; align-items: center; justify-content: space-between; gap: 32px; }
.footer__left { display: flex; align-items: center; gap: 14px; font-size: 12px; color: var(--mute); }
.footer__left > span { display: inline-flex; align-items: center; gap: 6px; }
.footer__right { font-size: 12px; color: var(--mute-2); }


/* Error toast — must stack above the see-all modal (z-index 9000 in ShopAllModal.vue) */
.purchase-toast {
    position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
    padding: 13px 28px; border-radius: 12px;
    font-size: 13px; font-weight: 700; white-space: nowrap;
    z-index: 9500; box-shadow: 0 8px 32px rgba(0,0,0,0.45);
    pointer-events: none;
}
.purchase-toast--error { background: rgba(244,63,94,0.95); color: #fff; }
.flash-enter-active, .flash-leave-active { transition: opacity 0.3s ease; }
.flash-enter-from, .flash-leave-to { opacity: 0; }

/* Purchase success modal — must stack above the see-all modal (z-index 9000 in ShopAllModal.vue) */
.success-backdrop {
    position: fixed; inset: 0; z-index: 9500;
    background: rgba(0,0,0,0.75); backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center; padding: 16px;
}
.success-modal {
    position: relative;
    background: #13131a; border: 1px solid rgba(255,255,255,0.10);
    border-radius: 24px; padding: 36px 32px; max-width: 460px; width: 100%;
    text-align: center;
}
.success-modal__badges {
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    margin: 0 0 20px;
}
.stat-badge {
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    padding: 6px 14px; border-radius: 999px;
    background: rgba(252,211,77,0.10); border: 1px solid rgba(252,211,77,0.25);
    color: var(--gold); font-size: 12px; font-weight: 700;
}
.stat-badge--hero {
    padding: 8px 18px; font-size: 15px;
    background: linear-gradient(135deg, rgba(251,191,36,0.18), rgba(252,211,77,0.06));
    border-color: rgba(252,211,77,0.40);
}
.success-modal__icon {
    width: 56px; height: 56px; border-radius: 50%;
    background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(252,211,77,0.08));
    border: 1px solid rgba(252,211,77,0.30);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 20px; color: var(--gold);
}
.success-modal__title {
    font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 800;
    letter-spacing: -0.02em; color: var(--fg); margin-bottom: 10px;
}
.success-modal__message {
    font-size: 14px; color: var(--mute); margin: 0 0 24px; line-height: 1.6;
}
.success-modal__cta { width: 100%; justify-content: center; }
.success-modal__link { color: var(--gold); text-underline-offset: 2px; }
.success-modal__link:hover { text-decoration: underline; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.25s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

/* Amount picker (free-form products) */
.pick-row { display: flex; gap: 6px; align-items: stretch; }
.amount-field { position: relative; flex: 1; display: flex; align-items: center; min-width: 0; }
.amount-field__icon { position: absolute; left: 10px; color: var(--mute); pointer-events: none; flex-shrink: 0; }
.amount-input {
    width: 100%; height: 36px; padding: 0 10px 0 28px; border-radius: 8px;
    background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.13);
    color: var(--fg); font-size: 13px; font-family: inherit;
}
.amount-input::placeholder { color: var(--mute); }
.amount-input:focus { outline: none; border-color: var(--accent, rgba(255,255,255,0.25)); box-shadow: 0 0 0 2px rgba(255,255,255,0.06); }
.amount-input::-webkit-outer-spin-button,
.amount-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.amount-input[type=number] { -moz-appearance: textfield; }
.amount-submit { height: 36px; padding: 0 14px; font-size: 12px; white-space: nowrap; }

/* OF link inside success modal */
.of-reveal__link {
    display: block; word-break: break-all; color: #67E8F9;
    font-size: 13px; font-weight: 600; text-decoration: none;
    padding: 12px; background: rgba(34,211,238,0.08);
    border: 1px solid rgba(34,211,238,0.25); border-radius: 10px;
}
.of-reveal__link:hover { text-decoration: underline; }
.of-reveal__code {
    display: block; word-break: break-all; color: #67E8F9;
    font-size: 16px; font-weight: 700; letter-spacing: 0.06em; font-family: monospace;
    text-align: center; padding: 14px 12px;
    background: rgba(34,211,238,0.08); border: 1px solid rgba(34,211,238,0.25); border-radius: 10px;
}
.of-reveal__link--amber,
.of-reveal__code--amber {
    color: #FDBA74;
    background: rgba(251,146,60,0.08); border-color: rgba(251,146,60,0.25);
}
.of-reveal__link--lime,
.of-reveal__code--lime {
    color: #A3E635;
    background: rgba(163,230,53,0.08); border-color: rgba(163,230,53,0.25);
}

.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn--loading { opacity: 0.80; }

.btn-spinner {
    display: inline-block;
    width: 14px; height: 14px;
    border: 2px solid rgba(255,255,255,0.25);
    border-top-color: currentColor;
    border-radius: 50%;
    animation: btn-spin 0.65s linear infinite;
    flex-shrink: 0;
}
@keyframes btn-spin { to { transform: rotate(360deg); } }

/* Anims */
@keyframes twinkle {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%      { opacity: 0.65; transform: scale(0.95); }
}
@keyframes drift-a {
    0%   { transform: translate(0px, 0px)   rotate(0deg)   scale(1); opacity: 0.20; }
    25%  { transform: translate(12px, -18px) rotate(15deg) scale(1.10); opacity: 0.35; }
    50%  { transform: translate(-8px, -28px) rotate(-8deg) scale(0.95); opacity: 0.18; }
    75%  { transform: translate(-14px, -10px) rotate(20deg) scale(1.05); opacity: 0.28; }
    100% { transform: translate(0px, 0px)   rotate(0deg)   scale(1); opacity: 0.20; }
}
@keyframes drift-b {
    0%   { transform: translate(0px, 0px)    rotate(0deg)   scale(1); opacity: 0.20; }
    30%  { transform: translate(-16px, -12px) rotate(-18deg) scale(1.08); opacity: 0.30; }
    60%  { transform: translate(10px, -22px)  rotate(10deg)  scale(0.92); opacity: 0.22; }
    100% { transform: translate(0px, 0px)    rotate(0deg)   scale(1); opacity: 0.20; }
}
@keyframes drift-c {
    0%   { transform: translate(0px, 0px)   rotate(0deg)   scale(1); opacity: 0.15; }
    40%  { transform: translate(18px, -14px) rotate(25deg)  scale(1.12); opacity: 0.28; }
    70%  { transform: translate(6px, -30px)  rotate(-12deg) scale(0.90); opacity: 0.12; }
    100% { transform: translate(0px, 0px)   rotate(0deg)   scale(1); opacity: 0.15; }
}
@keyframes star-float {
    0%, 100% { transform: translateY(0) rotate(-4deg) scale(1);    filter: drop-shadow(0 0 8px rgba(251,191,36,0.55)); }
    40%      { transform: translateY(-6px) rotate(4deg) scale(1.08); filter: drop-shadow(0 0 18px rgba(251,191,36,0.90)); }
    70%      { transform: translateY(-3px) rotate(0deg) scale(1.04); filter: drop-shadow(0 0 12px rgba(251,191,36,0.70)); }
}

/* ─── Responsive ─── */
@media (max-width: 768px) {
    /* Hero */
    .hero { padding: 40px 16px 36px; }
    .hero__grid { grid-template-columns: 1fr; gap: 32px; }
    .hero__h1 { font-size: 38px; }
    .hero__sub { font-size: 15px; }
    .hero__star { display: none; }
    .hero__glow { display: none; }

    /* Balance card */
    .balance-card__num { font-size: 44px; }

    /* Featured */
    .featured { padding: 0 16px; }
    .featured__bg-img { display: none; }
    .featured__copy { padding: 28px 24px; gap: 14px; }
    .featured__h2 { font-size: 34px; }
    .featured__sub { font-size: 15px; }
    .featured__actions { flex-direction: column; }
    /* Direct-child combinator only — a descendant selector here also matched
       the same-colored preset buttons nested inside .pick-row (25/50/100),
       forcing each onto its own full-width row instead of sharing one. */
    .featured__actions > .btn--blue,
    .featured__actions > .btn--gold,
    .featured__actions > .btn--violet,
    .featured__actions > .btn--emerald,
    .featured__actions > .btn--ghost { width: 100%; justify-content: center; }
    /* The 3 preset buttons share one row (evenly split); the custom-amount
       input + submit arrow (a pair, not meant to split) wrap to their own
       row below instead of all 5 controls squeezing onto one line and
       crushing the input to near-zero width. */
    .pick-row { flex-wrap: wrap; }
    .pick-row > button:not(.amount-submit) { flex: 1 1 0; }
    /* Not flex-basis: 100% — that forces a wrap after the presets (good) but
       leaves no room on that same line for amount-submit, which then wraps
       AGAIN onto a 3rd row by itself. Reserving space for the submit button's
       own width instead lets both land on the same second row. */
    .pick-row .amount-field { flex: 1 1 calc(100% - 70px); }
    /* Same swap the row's own card makes at this breakpoint: one full-width
       tile visible at a time via swipe, instead of shrinking 3 tiles to fit. */
    .mc-featured-option { flex: 0 0 100%; max-width: none; }
    .mc-featured-options--scrollable { justify-content: flex-start; }
    .kpi-row { flex-wrap: wrap; gap: 12px; }
    .kpi__sep { display: none; }

    /* Featured progress */
    .fp__n { font-size: 26px; }

    /* Catalog header */
    .catalog-head { padding: 48px 16px 32px; }
    .display { font-size: 36px; }

    /* Rows container */
    .rows { padding: 0 16px; }

    /* Individual row */
    .row { padding: 20px 16px; }
    .row__head { flex-direction: column; gap: 12px; }
    .row__h3 { font-size: 24px; }
    .row__sub { font-size: 14px; }
    .row__foot { flex-direction: column; align-items: flex-start; gap: 10px; }

    /* Grids */
    .grid--3 { grid-template-columns: repeat(2, 1fr); }
    .grid--4 { grid-template-columns: repeat(2, 1fr); }
    .grid--5 { grid-template-columns: repeat(2, 1fr); }

    /* Vault Clips carousel: 2 cards visible, arrows hidden — swipe/scroll
       is the primary interaction on mobile, and the arrows overlap the card
       thumbnails at this width instead of sitting clear of them like on
       desktop. */
    .vault-carousel__card { flex-basis: calc((100% - 14px) / 2); }
    .vault-carousel__arrow { display: none; }

    /* Series covers smaller on 2-col */
    .card__cover { height: 140px; }
    .card__cover--sm { height: 80px; }

    /* Staircase */
    .staircase { padding-left: 16px; padding-right: 16px; }
    .staircase__head { margin-bottom: 28px; }
    .staircase__grid { display: grid; grid-template-columns: repeat(2, 1fr); align-items: stretch; gap: 10px; overflow-x: visible; scroll-snap-type: none; }
    .pack { flex: unset; height: auto !important; min-height: 140px; }

    /* Footer */
    .footer { padding: 48px 16px 40px; }
    .footer__inner { flex-direction: column; align-items: flex-start; gap: 12px; }

    /* Bounty name */
    .bounty__name { font-size: 16px; }
}

@media (max-width: 480px) {
    .hero__h1 { font-size: 30px; }
    .display { font-size: 28px; }
    .featured__h2 { font-size: 28px; }
    .grid--3 { grid-template-columns: 1fr; }
    .grid--4 { grid-template-columns: 1fr; }

    /* Vault Clips carousel: peek the next card on mobile */
    .vault-carousel__card { flex-basis: 82%; }
}

/* ─── See all button ─── */
.row-see-all__link {
    background: none; border: none; cursor: pointer;
    color: #FCD34D;
    font-size: 12px; font-weight: 600;
    letter-spacing: 0.03em;
    padding: 0;
    transition: opacity 0.15s;
}
.row-see-all__link:hover { opacity: 0.75; }

/* ─── Empty state ─── */
.shop-empty {
    display: flex; flex-direction: column; align-items: center;
    text-align: center;
    padding: 80px 24px;
    color: #fff;
}
.shop-empty__icon {
    opacity: 0.25;
    margin-bottom: 24px;
    animation: pulse-star 2.4s ease-in-out infinite;
}
@keyframes pulse-star {
    0%, 100% { opacity: 0.2; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(1.08); }
}
.shop-empty__h {
    font-size: 28px; font-weight: 700;
    margin: 0 0 12px;
}
.shop-empty__sub {
    font-size: 15px; color: #9CA3AF;
    max-width: 380px; margin: 0;
    line-height: 1.6;
}
</style>

